/**
 * PLC Control Popup System
 * 
 * Endüstriyel ekipman kontrolü için modal popup yöneticisi.
 * Motor, vana, pompa gibi cihazları PLC üzerinden kontrol eder.
 * 
 * Popup Tipleri:
 *   control-setbool      → Basit Bool yazma (Start/Stop)
 *   control-setreal      → Basit Real değer yazma
 *   control-onoff        → Motor Start/Stop (suffix: _Mstr)
 *   control-onoff-auto   → Modlu motor (suffix: _Au, _Mstr)
 *   control-onf3         → ONF3 motor (suffix: _Mstr, _Mval)
 *   control-proportional → Oransal kontrol (suffix: _Aval, _Mval, _Au, _Mstr, _Res)
 *   control-or2/3/5/6    → Sabit legacy oransal kontrol profilleri
 *   control-onf4/5/6     → Sabit legacy On/Off kontrol profilleri
 *   control-setpoint     → Tek set değeri (suffix: _Aval)
 *   control-cav          → CAV set değeri + Vmin/Vmax gösterimi
 */

(function() {
  'use strict';

  // ============================================================
  // API Helper
  // ============================================================

  const API_BASE = '/admin/api/nexbus';

  // Auth token ve kullanıcı bilgisi (auth.js'den)
  function getAuthHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    if (window.Auth && typeof window.Auth.getToken === 'function') {
      const token = window.Auth.getToken();
      if (token) headers['Authorization'] = 'Bearer ' + token;
    }
    return headers;
  }

  function getCurrentUser() {
    if (window.Auth && typeof window.Auth.getUserInfo === 'function') {
      const info = window.Auth.getUserInfo();
      return (info && info.UserName) || 'operator';
    }
    return 'operator';
  }

  function buildVerificationPayload(tagName, value, options = {}) {
    const mode = options.verificationMode || 'readback';
    const payload = {
      verification_mode: mode,
      source: options.source || 'control-ui'
    };
    if (mode === 'readback') {
      payload.verification_tag = options.verificationTag || tagName;
      payload.verification_expected_value = Object.prototype.hasOwnProperty.call(options, 'expectedValue')
        ? options.expectedValue
        : value;
      if (options.verificationTimeoutMs) payload.verification_timeout_ms = options.verificationTimeoutMs;
    }
    return payload;
  }

  async function plcWriteBool(tagName, value, user, oldValue, displayName, options = {}) {
    const res = await fetch(`${API_BASE}/plc/write/bool`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        tag_name: tagName,
        value: !!value,
        user: user || getCurrentUser(),
        old_value: oldValue,
        display_name: displayName,
        ...buildVerificationPayload(tagName, !!value, options)
      })
    });
    if (res.status === 401) { showNotify('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.', 'error'); return { success: false, error: 'Unauthorized' }; }
    const result = await res.json();
    return result;
  }

  async function plcWriteReal(tagName, value, user, oldValue, displayName, options = {}) {
    const parsedValue = parseFloat(value);
    const res = await fetch(`${API_BASE}/plc/write/real`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        tag_name: tagName,
        value: parsedValue,
        user: user || getCurrentUser(),
        old_value: oldValue,
        display_name: displayName,
        ...buildVerificationPayload(tagName, parsedValue, options)
      })
    });
    if (res.status === 401) { showNotify('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.', 'error'); return { success: false, error: 'Unauthorized' }; }
    const result = await res.json();
    return result;
  }

  async function plcWriteInt(tagName, value, user, oldValue, displayName, options = {}) {
    const parsedValue = parseInt(value, 10);
    const res = await fetch(`${API_BASE}/plc/write/int`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        tag_name: tagName,
        value: parsedValue,
        user: user || getCurrentUser(),
        old_value: oldValue,
        display_name: displayName,
        ...buildVerificationPayload(tagName, parsedValue, options)
      })
    });
    if (res.status === 401) { showNotify('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.', 'error'); return { success: false, error: 'Unauthorized' }; }
    const result = await res.json();
    return result;
  }

  const PLC_SINGLE_RETRY_DELAYS_MS = [500, 1200];

  async function postPlcWriteBatch(writes, user) {
    const verifiedWrites = writes.map(write => ({
      ...write,
      ...buildVerificationPayload(write.tag_name, write.value, {
        verificationMode: write.verification_mode || 'readback',
        verificationTag: write.verification_tag || write.tag_name,
        expectedValue: Object.prototype.hasOwnProperty.call(write, 'verification_expected_value')
          ? write.verification_expected_value
          : write.value,
        verificationTimeoutMs: write.verification_timeout_ms,
        source: write.source || 'control-popup-batch'
      })
    }));
    const res = await fetch(`${API_BASE}/plc/write/batch`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ writes: verifiedWrites, user: user || getCurrentUser(), source: 'control-popup-batch' })
    });
    if (res.status === 401) { showNotify('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.', 'error'); return { success: false, error: 'Unauthorized' }; }
    return res.json();
  }

  function getFailedBatchWrites(writes, result) {
    if (result && result.success === true) return [];
    // PLC'nin kabul ettiği veya sonucu belirsiz kalan bir yazmayı yeniden
    // göndermek güvenli değildir. Yalnız accepted:false ile kesin reddedilenler retry edilir.
    const rejectedTags = new Set((result && Array.isArray(result.results) ? result.results : [])
      .filter(item => item && item.accepted === false)
      .map(item => item.tag_name));
    return writes.filter(write => rejectedTags.has(write.tag_name));
  }

  async function postSinglePlcWrite(write, user) {
    const dataType = String(write.data_type || '').toLowerCase();
    const options = {
      verificationMode: write.verification_mode || 'readback',
      verificationTag: write.verification_tag || write.tag_name,
      expectedValue: Object.prototype.hasOwnProperty.call(write, 'verification_expected_value')
        ? write.verification_expected_value
        : write.value,
      verificationTimeoutMs: write.verification_timeout_ms,
      source: write.source || 'control-popup-batch-retry'
    };
    let result;
    if (dataType === 'bool') {
      result = await plcWriteBool(write.tag_name, write.value, user, write.old_value, write.display_name, options);
    } else if (dataType === 'int') {
      result = await plcWriteInt(write.tag_name, write.value, user, write.old_value, write.display_name, options);
    } else if (dataType === 'real') {
      result = await plcWriteReal(write.tag_name, write.value, user, write.old_value, write.display_name, options);
    } else {
      return { tag_name: write.tag_name, success: false, error: `Desteklenmeyen veri tipi: ${write.data_type}` };
    }
    return {
      ...(result || {}),
      tag_name: write.tag_name,
      success: !!(result && result.success === true)
    };
  }

  async function retryWriteThroughSingleEndpoint(write, user) {
    let lastResult = { tag_name: write.tag_name, success: false, error: 'PLC yazma sonucu alınamadı' };
    for (const delayMs of PLC_SINGLE_RETRY_DELAYS_MS) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
      try {
        lastResult = await postSinglePlcWrite(write, user);
      } catch (error) {
        lastResult = {
          tag_name: write.tag_name,
          success: false,
          error: error && error.message ? error.message : 'PLC yazma isteği başarısız'
        };
      }
      // İlk tekil denemede PLC kabulü veya belirsiz bir sonuç alındıysa komutu
      // yeniden yollama. Yalnız accepted:false güvenle tekrar denenebilir.
      if (lastResult.success || lastResult.accepted !== false || lastResult.error === 'Unauthorized') break;
    }
    return lastResult;
  }

  async function plcWriteBatch(writes, user) {
    // Popup önce batch yolunu kullanır. Batch içinde geçici hata alan mutlak
    // Bool/Int/Real setler, normal ekrandaki kararlı tekil endpoint'lerden sıralı
    // olarak yeniden gönderilir. Başarılı tag'ler ikinci kez yazılmaz.
    let firstResult = null;
    let firstError = null;
    try {
      firstResult = await postPlcWriteBatch(writes, user);
    } catch (error) {
      firstError = error;
    }

    if (firstResult && firstResult.error === 'Unauthorized') return firstResult;
    if (firstError) {
      return {
        success: false,
        accepted: null,
        status: 'failed',
        error: firstError && firstError.message ? firstError.message : 'PLC batch yazma sonucu belirsiz',
        results: writes.map(write => ({ tag_name: write.tag_name, success: false, accepted: null, status: 'failed', error: 'Yazma sonucu belirsiz; güvenlik nedeniyle otomatik tekrar yapılmadı' }))
      };
    }
    const failedWrites = getFailedBatchWrites(writes, firstResult);
    if (failedWrites.length === 0) return firstResult;

    showNotify(`${failedWrites.length} PLC yazma işlemi geçici olarak başarısız oldu; tekil yazma yoluyla yeniden deneniyor...`, 'warning');
    const retryResults = [];
    for (const write of failedWrites) {
      retryResults.push(await retryWriteThroughSingleEndpoint(write, user));
    }

    const firstResults = firstResult && Array.isArray(firstResult.results) ? firstResult.results : [];
    const firstByTag = new Map(firstResults.map(item => [item.tag_name, item]));
    const retryByTag = new Map(retryResults.map(item => [item.tag_name, item]));
    const failedTagNames = new Set(failedWrites.map(write => write.tag_name));
    const mergedResults = writes.map(write => {
      if (!failedTagNames.has(write.tag_name)) return firstByTag.get(write.tag_name) || { tag_name: write.tag_name, success: true };
      return retryByTag.get(write.tag_name) || {
        tag_name: write.tag_name,
        success: false,
        error: (firstByTag.get(write.tag_name) || {}).error || (firstError && firstError.message) || 'PLC yazma başarısız'
      };
    });

    return {
      success: mergedResults.every(item => item.success),
      results: mergedResults,
      retry_attempted: true,
      retry_count: failedWrites.length,
      retry_strategy: 'single-endpoint'
    };
  }

  function notifyBatchResult(result, successText, errorText) {
    if (result && result.success === true) {
      showNotify(successText || 'Değişiklikler uygulandı ve geri okumadan doğrulandı.', 'success');
      return true;
    }
    const items = result && Array.isArray(result.results) ? result.results : [];
    const acceptedUnverified = items.filter(item => item && item.accepted === true && item.success !== true);
    if (acceptedUnverified.length) {
      const details = acceptedUnverified.map(item => {
        const verification = item.verification || {};
        const actual = verification.actual_value === null || verification.actual_value === undefined
          ? 'okunamadı'
          : verification.actual_value;
        return `${item.tag_name} (beklenen: ${verification.expected_value}, okunan: ${actual})`;
      });
      showNotify('PLC kabul etti fakat geri okuma doğrulanamadı: ' + details.join(', '), 'warning');
      return false;
    }
    const rejected = items.filter(item => item && item.success !== true)
      .map(item => item.tag_name + (item.error ? ` (${item.error})` : ''));
    showNotify(rejected.length ? `${errorText || 'Yazma işlemi başarısız'}: ${rejected.join(', ')}` : (errorText || 'Yazma işlemi başarısız'), 'error');
    return false;
  }

  async function plcReadTags(tags) {
    const res = await fetch(`${API_BASE}/plc/read`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tags })
    });
    return res.json();
  }

  // ============================================================
  // Toast Notification
  // ============================================================

  function showNotify(message, type) {
    // type: 'success' | 'error' | 'info' | 'warning'
    const colors = { success: '#2eb85c', error: '#e55353', info: '#3399ff', warning: '#f9b115' };
    const icons = { success: 'fa-check-circle', error: 'fa-times-circle', info: 'fa-info-circle', warning: 'fa-exclamation-triangle' };

    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed; top: 20px; right: 20px; z-index: 200000;
      background: ${colors[type] || colors.info}; color: #fff;
      padding: 12px 20px; border-radius: 6px; font-size: 14px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3); display: flex; align-items: center; gap: 8px;
      animation: controlPopupSlideIn 0.3s ease; max-width: 400px;
    `;
    toast.innerHTML = `<i class="fas ${icons[type] || icons.info}"></i> ${escapeHtml(message)}`;
    document.body.appendChild(toast);
    setTimeout(() => { toast.style.opacity = '0'; toast.style.transition = 'opacity 0.3s'; }, 2500);
    setTimeout(() => toast.remove(), 3000);
  }

  function escapeHtml(str) {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  // ============================================================
  // Popup Registry — tüm popup tipleri burada kayıtlı
  // ============================================================

  const POPUP_TYPES = {};

  // PLC'ye yazma yapan popup'lar `user` rolünde hiç açılmamalıdır. Bu kontrolü
  // popup yöneticisinde tutmak, dashboard tıklaması dışındaki (legacy yönlendirme
  // gibi) açılış yollarının da SetReal/SetBool ile aynı yetki politikasına
  // uymasını sağlar. Sensor Chart yalnızca izleme amacıyla açık kalır.
  const PLC_WRITE_POPUP_TYPES = new Set([
    'control-setbool',
    'control-setreal',
    'control-onoff',
    'control-onoff-auto',
    'control-onf3',
    'control-onf4',
    'control-onf5',
    'control-onf6',
    'control-proportional',
    'control-or2',
    'control-or3',
    'control-setpoint',
    'control-or5',
    'control-or6',
    'control-cav',
    'onf3',
    'onf4',
    'onf5',
    'onf6',
    'or2',
    'or3',
    'or5',
    'or6',
    'cav'
  ]);

  function registerPopupType(name, builder) {
    POPUP_TYPES[name] = builder;
  }

  // ============================================================
  // ControlPopupManager
  // ============================================================

  class ControlPopupManager {
    constructor() {
      this._modalEl = null;
      this._backdrop = null;
      this._currentConfig = null;
      this._outsidePointerHandler = null;
      this._outsidePointerTimer = null;
      this._injectStyles();
    }

    /**
     * Popup aç
     * @param {string} popupType - Popup tipi (control-setbool, control-onoff, vb.)
     * @param {object} config - Popup konfigürasyonu
     * @param {string} config.rootTag - Cihaz ana tag adı
     * @param {string} [config.title] - Başlık
     * @param {number} [config.authLevel] - Yetki seviyesi
     * @param {object} [config.suffixes] - Özel suffix'ler
     * @param {object} [config.labels] - Özel buton etiketleri
    */
    async open(popupType, config) {
      const normalizedPopupType = String(popupType || '').toLowerCase();
      const builder = POPUP_TYPES[popupType] || POPUP_TYPES[normalizedPopupType];
      if (!builder) {
        showNotify(`Bilinmeyen popup tipi: ${popupType}`, 'error');
        return;
      }

      // Auth kontrolü — giriş yapmamış kullanıcılar popup açamaz
      if (window.Auth && typeof window.Auth.isAuthenticated === 'function') {
        if (!window.Auth.isAuthenticated()) {
          showNotify('Bu işlem için giriş yapmanız gerekiyor', 'warning');
          return;
        }
      }

      // SetReal/SetBool ile aynı rol kuralı: user göremez; operator ve üzeri açabilir.
      if (PLC_WRITE_POPUP_TYPES.has(normalizedPopupType)
        && window.Auth
        && typeof window.Auth.canWritePlc === 'function'
        && !window.Auth.canWritePlc()) {
        showNotify('Bu işlem için yetkiniz yok (Operator veya üzeri rol gerekir).', 'warning');
        return;
      }

      this._currentConfig = config;
      this.close(); // Önceki popup'ı kapat

      const content = await builder(config, this);
      this._show(config.title || config.rootTag || 'Kontrol', content);
    }

    /** Modal göster */
    _show(title, bodyHtml) {
      // Backdrop
      this._backdrop = document.createElement('div');
      this._backdrop.className = 'cpopup-backdrop';
      this._backdrop.onclick = () => this.close();
      document.body.appendChild(this._backdrop);

      // Modal
      this._modalEl = document.createElement('div');
      this._modalEl.className = 'cpopup-modal';
      this._modalEl.innerHTML = `
        <div class="cpopup-header">
          <span class="cpopup-title">${escapeHtml(title)}</span>
          <button class="cpopup-close" title="Kapat">&times;</button>
        </div>
        <div class="cpopup-body">${bodyHtml}</div>
      `;
      document.body.appendChild(this._modalEl);

      // innerHTML ile eklenen <script> tagları otomatik çalışmaz — manuel çalıştır
      this._modalEl.querySelectorAll('script').forEach(oldScript => {
        const newScript = document.createElement('script');
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });

      // Close button
      this._modalEl.querySelector('.cpopup-close').onclick = () => this.close();

      // Modal dışındaki tıklamaları backdrop'tan bağımsız olarak yakala.
      // Listener bir sonraki event turunda bağlanır; popup'ı açan tıklama popup'ı hemen kapatmaz.
      const outsidePointerHandler = event => {
        if (!this._modalEl || this._modalEl.contains(event.target)) return;
        this.close();
      };
      this._outsidePointerHandler = outsidePointerHandler;
      this._outsidePointerTimer = setTimeout(() => {
        this._outsidePointerTimer = null;
        if (this._modalEl && this._outsidePointerHandler === outsidePointerHandler) {
          document.addEventListener('pointerdown', outsidePointerHandler, true);
        }
      }, 0);

      // Animasyon
      requestAnimationFrame(() => {
        this._backdrop.classList.add('visible');
        this._modalEl.classList.add('visible');
      });
    }

    /** Modal kapat */
    close() {
      if (this._outsidePointerTimer !== null) {
        clearTimeout(this._outsidePointerTimer);
        this._outsidePointerTimer = null;
      }
      if (this._outsidePointerHandler) {
        document.removeEventListener('pointerdown', this._outsidePointerHandler, true);
        this._outsidePointerHandler = null;
      }
      if (this._modalEl) {
        this._modalEl.remove();
        this._modalEl = null;
      }
      if (this._backdrop) {
        this._backdrop.remove();
        this._backdrop = null;
      }
      this._currentConfig = null;
    }

    /** Mevcut modal body'sine erişim */
    getBody() {
      return this._modalEl ? this._modalEl.querySelector('.cpopup-body') : null;
    }

    /** CSS injection (tek sefer) */
    _injectStyles() {
      if (document.getElementById('cpopup-styles')) return;
      const style = document.createElement('style');
      style.id = 'cpopup-styles';
      style.textContent = `
        @keyframes controlPopupSlideIn {
          from { transform: translateX(60px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .cpopup-backdrop {
          position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 100000; opacity: 0; transition: opacity 0.2s;
        }
        .cpopup-backdrop.visible { opacity: 1; }
        .cpopup-modal {
          position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) scale(0.9);
          z-index: 100001; background: #1e2130; color: #e4e6eb; border-radius: 10px;
          min-width: 320px; max-width: 90vw; box-shadow: 0 8px 32px rgba(0,0,0,0.5);
          opacity: 0; transition: all 0.25s ease;
        }
        .cpopup-modal.visible { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        .cpopup-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px 18px; border-bottom: 1px solid #333654;
          background: #262940; border-radius: 10px 10px 0 0;
        }
        .cpopup-title { font-size: 16px; font-weight: 600; }
        .cpopup-close {
          background: none; border: none; color: #aaa; font-size: 22px; cursor: pointer;
          width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
          border-radius: 4px;
        }
        .cpopup-close:hover { background: #e55353; color: #fff; }
        .cpopup-body { padding: 18px; }

        /* Kontrol butonları */
        .cpopup-btn-row { display: flex; gap: 10px; justify-content: center; margin-bottom: 14px; }
        .cpopup-btn {
          padding: 10px 24px; border: none; border-radius: 6px; font-size: 14px; font-weight: 600;
          cursor: pointer; min-width: 100px; transition: all 0.15s; color: #fff;
          display: flex; align-items: center; justify-content: center; gap: 6px;
        }
        .cpopup-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .cpopup-btn-start { background: #2eb85c; }
        .cpopup-btn-start:hover:not(:disabled) { background: #25a34e; }
        .cpopup-btn-start.active { background: #2eb85c; box-shadow: 0 0 12px rgba(46,184,92,0.5); }
        .cpopup-btn-stop { background: #e55353; }
        .cpopup-btn-stop:hover:not(:disabled) { background: #d13e3e; }
        .cpopup-btn-stop.active { background: #e55353; box-shadow: 0 0 12px rgba(229,83,83,0.5); }
        .cpopup-btn-auto { background: #3399ff; }
        .cpopup-btn-auto:hover:not(:disabled) { background: #2280e0; }
        .cpopup-btn-auto.active { background: #3399ff; box-shadow: 0 0 12px rgba(51,153,255,0.5); }
        .cpopup-btn-man { background: #f9b115; color: #1e2130; }
        .cpopup-btn-man:hover:not(:disabled) { background: #e0a00e; }
        .cpopup-btn-man.active { background: #f9b115; box-shadow: 0 0 12px rgba(249,177,21,0.5); color: #1e2130; }
        .cpopup-btn-reset { background: #636f83; }
        .cpopup-btn-reset:hover:not(:disabled) { background: #555f70; }
        .cpopup-btn-apply { background: #321fdb; }
        .cpopup-btn-apply:hover:not(:disabled) { background: #2819b0; }

        /* Input alanları */
        .cpopup-input-group { margin-bottom: 14px; }
        .cpopup-input-group label {
          display: block; font-size: 12px; color: #8a8ea8; margin-bottom: 4px; text-transform: uppercase;
        }
        .cpopup-input-group input {
          width: 100%; padding: 8px 12px; border: 1px solid #3a3d52; border-radius: 6px;
          background: #2a2d3e; color: #e4e6eb; font-size: 16px; outline: none;
        }
        .cpopup-input-group input:focus { border-color: #321fdb; }

        /* Spinner */
        .cpopup-spinner {
          width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff; border-radius: 50%;
          animation: cpopupSpin 0.6s linear infinite; display: inline-block;
        }
        @keyframes cpopupSpin { to { transform: rotate(360deg); } }

        /* Sensör popup alarm formu */
        .cpopup-sensor-shell {
          min-width: 780px; width: 80vw; max-width: 1100px;
          max-height: calc(90vh - 100px); overflow-y: auto; padding-right: 2px;
        }
        .cpopup-alert-card { padding: 4px 2px 2px; }
        .cpopup-alert-heading {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          margin-bottom: 10px;
        }
        .cpopup-alert-note {
          padding: 9px 11px; margin-bottom: 12px; border: 1px solid #333654; border-radius: 5px;
          background: #262940; color: #a7aabd; font-size: 11px;
        }
        .cpopup-alert-grid { display: grid; grid-template-columns: repeat(12, 1fr); gap: 12px; }
        .cpopup-alert-field { grid-column: span 12; min-width: 0; }
        .cpopup-alert-field.span-8 { grid-column: span 8; }
        .cpopup-alert-field.span-6 { grid-column: span 6; }
        .cpopup-alert-field.span-4 { grid-column: span 4; }
        .cpopup-alert-field.span-3 { grid-column: span 3; }
        .cpopup-alert-field label {
          display: block; margin-bottom: 5px; color: #c7c9d6; font-size: 12px; font-weight: 500;
        }
        .cpopup-alert-input, .cpopup-alert-select {
          width: 100%; min-height: 36px; padding: 7px 10px; border: 1px solid #3a3d52;
          border-radius: 5px; background: #242738; color: #e4e6eb; font-size: 13px; outline: none;
        }
        .cpopup-alert-input:focus, .cpopup-alert-select:focus { border-color: #3399ff; }
        .cpopup-alert-input[readonly] { color: #9fd0ff; background: #202334; cursor: not-allowed; }
        .cpopup-alert-select-row { display: flex; gap: 6px; }
        .cpopup-alert-select-row .cpopup-alert-select { flex: 1; }
        .cpopup-alert-mini-btn {
          width: 36px; border: 1px solid #3a3d52; border-radius: 5px; background: #2a2d3e;
          color: #a7aabd; cursor: pointer;
        }
        .cpopup-alert-mini-btn:hover:not(:disabled) { color: #fff; border-color: #3399ff; }
        .cpopup-alert-check {
          display: flex; align-items: center; gap: 7px; min-height: 36px; color: #d5d7e2; font-size: 12px;
        }
        .cpopup-alert-check input { width: 16px; height: 16px; accent-color: #5856d6; }
        .cpopup-alert-section {
          grid-column: span 12; padding-top: 9px; border-top: 1px solid #333654;
          color: #3399ff; font-size: 11px; font-weight: 600;
        }
        .cpopup-alert-actions {
          display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; padding-top: 12px;
          border-top: 1px solid #333654;
        }
        .cpopup-alert-action {
          min-width: 100px; padding: 8px 14px; border: 0; border-radius: 5px; color: #fff;
          font-size: 12px; font-weight: 600; cursor: pointer;
        }
        .cpopup-alert-action:disabled, .cpopup-alert-mini-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .cpopup-alert-action.secondary { background: #636f83; }
        .cpopup-alert-action.primary { background: #5856d6; }
        @media (max-width: 900px) {
          .cpopup-sensor-shell { min-width: 0; width: 84vw; }
          .cpopup-alert-field.span-8, .cpopup-alert-field.span-6, .cpopup-alert-field.span-4,
          .cpopup-alert-field.span-3 { grid-column: span 12; }
        }

        /* Ayırıcı */
        .cpopup-divider { border-top: 1px solid #333654; margin: 14px 0; }

        /* Footer */
        .cpopup-footer { display: flex; gap: 10px; justify-content: center; margin-top: 14px; }

        /* Inactive button (toggled OFF state) */
        .cpopup-btn-inactive { background: #3a3d52 !important; box-shadow: none !important; }

        /* Onay dialogu */
        .cpopup-confirm-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.6);
          z-index: 200001; display: flex; align-items: center; justify-content: center;
        }
        .cpopup-confirm-box {
          background: #1e2130; border-radius: 10px; padding: 24px; min-width: 300px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5); text-align: center; color: #e4e6eb;
        }
        .cpopup-confirm-box .cpopup-confirm-msg { font-size: 15px; margin-bottom: 18px; }
        .cpopup-confirm-box .cpopup-confirm-actions { display: flex; gap: 10px; justify-content: center; }

        /* Validation hatası */
        .cpopup-input-error { border-color: #e55353 !important; }
        .cpopup-error-text { color: #e55353; font-size: 11px; margin-top: 2px; }
      `;
      (document.head || document.documentElement).appendChild(style);
    }
  }

  // ============================================================
  // Onay Dialogu (Confirm)
  // ============================================================

  function showConfirm(message) {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'cpopup-confirm-overlay';
      overlay.innerHTML = `
        <div class="cpopup-confirm-box">
          <div class="cpopup-confirm-msg"><i class="fas fa-exclamation-triangle" style="color:#f9b115;margin-right:6px;"></i>${escapeHtml(message)}</div>
          <div class="cpopup-confirm-actions">
            <button class="cpopup-btn cpopup-btn-stop" id="cpopup-confirm-no" style="min-width:80px;padding:8px 16px;">İptal</button>
            <button class="cpopup-btn cpopup-btn-start" id="cpopup-confirm-yes" style="min-width:80px;padding:8px 16px;">Onayla</button>
          </div>
        </div>
      `;
      document.body.appendChild(overlay);
      overlay.querySelector('#cpopup-confirm-yes').onclick = () => { overlay.remove(); resolve(true); };
      overlay.querySelector('#cpopup-confirm-no').onclick = () => { overlay.remove(); resolve(false); };
    });
  }

  // ============================================================
  // Helper: Buton durumunu spinner + doğrulama ile güncelle
  // ============================================================

  function valuesMatch(actualValue, expectedValue) {
    if (typeof expectedValue === 'boolean') {
      const normalizeBool = value => value === true || value === 'true' || value === 1 || value === '1';
      return normalizeBool(actualValue) === normalizeBool(expectedValue);
    }
    if (typeof expectedValue === 'number') {
      const actualNumber = Number(actualValue);
      if (!Number.isFinite(actualNumber)) return false;
      return Math.abs(actualNumber - expectedValue) <= Math.max(1e-6, Math.abs(expectedValue) * 1e-6);
    }
    return String(actualValue) === String(expectedValue);
  }

  async function waitForReadback(readTag, expectedValue, timeoutMs = 6000) {
    const startedAt = Date.now();
    let actualValue = null;
    while (Date.now() - startedAt < timeoutMs) {
      await new Promise(resolve => setTimeout(resolve, 300));
      try {
        const readResult = await plcReadTags([readTag]);
        const tagValue = readResult.tags && readResult.tags[readTag];
        if (tagValue !== undefined) {
          actualValue = typeof tagValue === 'object' ? tagValue.value : tagValue;
          if (valuesMatch(actualValue, expectedValue)) return { verified: true, actualValue };
        }
      } catch (e) { /* süre dolana kadar tekrar dene */ }
    }
    return { verified: false, actualValue };
  }

  function appliedMessage(message) {
    return String(message || 'İşlem uygulandı')
      .replace(/gönderildi/gi, 'uygulandı')
      .replace(/geçildi/gi, 'doğrulandı');
  }

  function verificationFailureMessage(result, fallback) {
    const verification = result && result.verification;
    if (!verification) return (result && (result.error || result.details)) || fallback || 'Geri okuma doğrulanamadı';
    const actual = verification.actual_value;
    const expected = verification.expected_value;
    const tag = verification.tag_name || '';
    const actualText = actual === null || actual === undefined ? 'okunamadı' : String(actual);
    return `PLC isteği kabul etti; fakat ${tag} geri okuması doğrulanamadı (beklenen: ${expected}, okunan: ${actualText}).`;
  }

  async function writeAndVerify(btn, writeFn, readTag, expectedValue, successMsg, errorMsg, skipConfirm) {
    // Onay dialogu (skipConfirm true değilse)
    if (!skipConfirm) {
      // Confirm mesajı: "gönderildi" → "gönderilecek", "geçildi" → "geçilecek" dönüşümü
      let confirmText = 'Bu PLC yazma işlemini onaylıyor musunuz?';
      if (successMsg) {
        const futureMsg = successMsg
          .replace(/gönderildi/gi, 'gönderilecek')
          .replace(/geçildi/gi, 'geçilecek')
          .replace(/başarıyla /gi, '');
        confirmText = `"${futureMsg}" işlemini gerçekleştirmek istiyor musunuz?`;
      }
      const confirmed = await showConfirm(confirmText);
      if (!confirmed) return false;
    }

    const originalHtml = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = `<span class="cpopup-spinner"></span><span>Yazılıyor...</span>`;
    const phaseTimer = setTimeout(() => {
      if (btn.disabled) btn.innerHTML = `<span class="cpopup-spinner"></span><span>Geri okuma doğrulanıyor...</span>`;
    }, 700);

    try {
      const result = await writeFn();
      if (!result || result.success !== true) {
        if (result && result.accepted === true) {
          showNotify(verificationFailureMessage(result, errorMsg), 'warning');
        } else {
          showNotify((result && (result.error || result.details)) || errorMsg || 'Yazma işlemi başarısız', 'error');
        }
        return false;
      }

      // Yeni transaction endpoint'i doğrulamayı sunucuda tamamlar. Eski bir
      // sunucuyla konuşuluyorsa aynı değeri istemci tarafında gerçekten karşılaştır.
      if (result.status === 'verified' || result.verified === true) {
        showNotify(appliedMessage(successMsg), 'success');
        return true;
      }
      if (result.status === 'accepted' && !readTag) {
        showNotify('Komut PLC tarafından kabul edildi.', 'success');
        return true;
      }
      if (readTag) {
        const fallbackVerification = await waitForReadback(readTag, expectedValue);
        if (!fallbackVerification.verified) {
          showNotify(`PLC isteği kabul etti; fakat ${readTag} geri okuması doğrulanamadı (beklenen: ${expectedValue}, okunan: ${fallbackVerification.actualValue ?? 'okunamadı'}).`, 'warning');
          return false;
        }
      }

      showNotify(appliedMessage(successMsg), 'success');
      return true;
    } catch (err) {
      showNotify((err && err.message) || errorMsg || 'Bağlantı hatası', 'error');
      return false;
    } finally {
      clearTimeout(phaseTimer);
      btn.innerHTML = originalHtml;
      btn.disabled = false;
    }
  }

  // Helper: toggle button active states
  function setToggleState(btnA, btnB, aActive) {
    if (aActive) {
      btnA.classList.add('active');
      btnA.classList.remove('cpopup-btn-inactive');
      btnB.classList.remove('active');
      btnB.classList.add('cpopup-btn-inactive');
    } else {
      btnB.classList.add('active');
      btnB.classList.remove('cpopup-btn-inactive');
      btnA.classList.remove('active');
      btnA.classList.add('cpopup-btn-inactive');
    }
  }

  // Tag'ın güncel değerini oku — önce plcReadTags, yoksa latestTagValues fallback
  async function readTagValue(tag) {
    try {
      const r = await plcReadTags([tag]);
      if (r.tags && r.tags[tag] !== undefined) {
        const v = r.tags[tag];
        return typeof v === 'object' ? v.value : v;
      }
    } catch (e) { /* */ }
    // Fallback: dashboard'daki canlı veri cache'i
    if (window.latestTagValues) {
      const v = window.latestTagValues.get(tag);
      if (v !== undefined) {
        if (v && typeof v === 'object') return v.Value !== undefined ? v.Value : v.value;
        return v;
      }
    }
    return null;
  }

  function toBooleanValue(value) {
    if (value === null || value === undefined || value === '') return null;
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  function toFiniteNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  async function readPopupTagValues(tags) {
    const requestedTags = [...new Set((tags || []).filter(Boolean))];
    const values = {};
    if (requestedTags.length === 0) return values;

    try {
      const result = await plcReadTags(requestedTags);
      const responseTags = result && result.tags ? result.tags : {};
      requestedTags.forEach(tag => {
        if (responseTags[tag] === undefined) return;
        const rawValue = responseTags[tag];
        values[tag] = rawValue && typeof rawValue === 'object' ? rawValue.value : rawValue;
      });
    } catch (error) {
      // Eksik değerler aşağıdaki cache destekli tek-tag okumasıyla tamamlanır.
    }

    for (const tag of requestedTags) {
      if (values[tag] !== undefined) continue;
      values[tag] = await readTagValue(tag);
    }
    return values;
  }

  function serializeInlineData(value) {
    return JSON.stringify(value).replace(/</g, '\\u003c');
  }

  // ============================================================
  // POPUP TİPİ: control-setbool
  // Basit Bool yazma — Start/Stop butonları
  // ============================================================

  registerPopupType('control-setbool', async (config) => {
    const tag = config.rootTag;
    const labels = config.labels || {};
    const displayName = config.title || config.displayName || tag;

    // Mevcut değeri oku
    let currentValue = await readTagValue(tag);

    const isBoolTrue = currentValue === true || currentValue === 'true' || currentValue === 1;
    const oldValStr = currentValue != null ? String(!!isBoolTrue) : 'null';

    return `
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-start ${isBoolTrue ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-set-true">
          <i class="fas fa-play"></i> ${escapeHtml(labels.startBtn || 'START')}
        </button>
        <button class="cpopup-btn cpopup-btn-stop ${!isBoolTrue ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-set-false">
          <i class="fas fa-stop"></i> ${escapeHtml(labels.stopBtn || 'STOP')}
        </button>
      </div>
      <div style="text-align:center; font-size:12px; color:#8a8ea8;">
        Tag: <span style="color:#e4e6eb;">${escapeHtml(tag)}</span>
      </div>
      <script>
        (function() {
          const btnTrue = document.getElementById('cpopup-set-true');
          const btnFalse = document.getElementById('cpopup-set-false');
          let curVal = ${isBoolTrue};
          btnTrue.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnTrue, 
              () => window._cpopupWriteBool('${tag}', true, null, curVal, '${displayName.replace(/'/g, "\\'")}'), 
              '${tag}', true, 'START komutu gönderildi', 'START başarısız');
            if (ok) { curVal = true; window._cpopupSetToggle(btnTrue, btnFalse, true); }
          };
          btnFalse.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnFalse,
              () => window._cpopupWriteBool('${tag}', false, null, curVal, '${displayName.replace(/'/g, "\\'")}'),
              '${tag}', false, 'STOP komutu gönderildi', 'STOP başarısız');
            if (ok) { curVal = false; window._cpopupSetToggle(btnTrue, btnFalse, false); }
          };
        })();
      </script>
    `;
  });

  // ============================================================
  // POPUP TİPİ: control-setreal
  // Real değer yazma — Input + Send butonu
  // ============================================================

  registerPopupType('control-setreal', async (config) => {
    const tag = config.rootTag;
    const verifyTag = config.verifyTag || tag;
    const labels = config.labels || {};
    const displayName = config.title || config.displayName || tag;
    const parseLimit = value => {
      if (value === null || value === undefined || value === '') return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };
    const minValue = parseLimit(config.minValue);
    const maxValue = parseLimit(config.maxValue);
    const rangeIsValid = minValue === null || maxValue === null || minValue <= maxValue;
    const confirmBeforeSend = config.confirmBeforeSend === true || config.confirmBeforeSend === 1 ||
      config.confirmBeforeSend === '1' || config.confirmBeforeSend === 'true';
    const rangeParts = [];
    if (minValue !== null) rangeParts.push('Min: ' + minValue);
    if (maxValue !== null) rangeParts.push('Max: ' + maxValue);
    const rangeHint = rangeParts.length
      ? ` <span style="color:#8a8ea8;font-weight:400;text-transform:none;">(${rangeParts.join(' · ')})</span>`
      : '';
    const minAttr = rangeIsValid && minValue !== null ? ` min="${minValue}"` : '';
    const maxAttr = rangeIsValid && maxValue !== null ? ` max="${maxValue}"` : '';

    let currentValue = await readTagValue(tag);
    if (currentValue === null) currentValue = '';

    return `
      <div class="cpopup-input-group">
        <label>${escapeHtml(labels.inputLabel || 'Set Değeri')}${rangeHint}</label>
        <input type="number" id="cpopup-real-input" value="${escapeHtml(currentValue)}" step="any"${minAttr}${maxAttr} />
        ${rangeIsValid ? '' : '<div class="cpopup-error-text">Minimum değer maksimum değerden büyük olamaz.</div>'}
      </div>
      <div class="cpopup-footer">
        <button class="cpopup-btn cpopup-btn-apply" id="cpopup-real-send" ${rangeIsValid ? '' : 'disabled'}>
          <i class="fas fa-paper-plane"></i> ${escapeHtml(labels.sendBtn || 'GÖNDER')}
        </button>
      </div>
      <div style="text-align:center; font-size:12px; color:#8a8ea8; margin-top:8px;">
        Yazma: <span style="color:#e4e6eb;">${escapeHtml(tag)}</span>
        ${verifyTag !== tag ? `<br>Geri okuma: <span style="color:#e4e6eb;">${escapeHtml(verifyTag)}</span>` : ''}
      </div>
      <script>
        (function() {
          const initVal = ${JSON.stringify(currentValue === '' ? '' : String(currentValue))};
          const minValue = ${JSON.stringify(minValue)};
          const maxValue = ${JSON.stringify(maxValue)};
          const confirmBeforeSend = ${JSON.stringify(confirmBeforeSend)};
          const input = document.getElementById('cpopup-real-input');

          function normalizeValue(showLimitMessage) {
            const rawValue = input.value.trim();
            if (rawValue === '' || !Number.isFinite(Number(rawValue))) return null;
            let value = Number(rawValue);
            let limitMessage = '';
            if (maxValue !== null && value > maxValue) {
              value = maxValue;
              limitMessage = 'Girilen değer maksimum ' + maxValue + ' olarak düzeltildi.';
            }
            if (minValue !== null && value < minValue) {
              value = minValue;
              limitMessage = 'Girilen değer minimum ' + minValue + ' olarak düzeltildi.';
            }
            if (String(value) !== rawValue) input.value = String(value);
            if (limitMessage && showLimitMessage) window._cpopupNotify(limitMessage, 'warning');
            return value;
          }

          input.addEventListener('change', () => normalizeValue(true));
          document.getElementById('cpopup-real-send').onclick = async () => {
            const normalizedValue = normalizeValue(true);
            if (normalizedValue === null) { window._cpopupNotify('Geçerli bir sayı giriniz', 'warning'); return; }
            const val = String(normalizedValue);
            const btn = document.getElementById('cpopup-real-send');
            const ok = await window._cpopupWriteAndVerify(btn,
              () => window._cpopupWriteReal('${tag}', normalizedValue, null, initVal !== '' ? parseFloat(initVal) : null, '${displayName.replace(/'/g, "\\'")}', { verificationTag: '${verifyTag.replace(/'/g, "\\'")}', expectedValue: normalizedValue, source: 'set-real' }),
              '${verifyTag.replace(/'/g, "\\'")}', normalizedValue, 'Değer uygulandı: ' + val, 'Değer gönderilemedi', !confirmBeforeSend);
            if (ok) window._cpopupManager.close();
          };
        })();
      </script>
    `;
  });

  // ============================================================
  // POPUP TİPİ: control-onoff
  // Motor On/Off — Start/Stop butonları (suffix: _Mstr)
  // ============================================================

  registerPopupType('control-onoff', async (config) => {
    const root = config.rootTag;
    const sfx = config.suffixes || {};
    const startTag = root + (sfx.start || '_Mstr');
    const labels = config.labels || {};
    const displayName = config.title || config.displayName || root;

    let isRunning = null;
    const rawVal = await readTagValue(startTag);
    if (rawVal !== null) {
      isRunning = rawVal === true || rawVal === 'true' || rawVal === 1;
    }

    return `
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-start ${isRunning === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onoff-start">
          <i class="fas fa-play"></i> ${escapeHtml(labels.startBtn || 'START')}
        </button>
        <button class="cpopup-btn cpopup-btn-stop ${isRunning === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onoff-stop">
          <i class="fas fa-stop"></i> ${escapeHtml(labels.stopBtn || 'STOP')}
        </button>
      </div>
      <div style="text-align:center; font-size:12px; color:#8a8ea8;">
        Tag: <span style="color:#e4e6eb;">${escapeHtml(startTag)}</span>
      </div>
      <script>
        (function() {
          const btnStart = document.getElementById('cpopup-onoff-start');
          const btnStop = document.getElementById('cpopup-onoff-stop');
          let curVal = ${isRunning === true};
          btnStart.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnStart,
              () => window._cpopupWriteBool('${startTag}', true, null, curVal, '${displayName.replace(/'/g, "\\'")}'),
              '${startTag}', true, 'START komutu gönderildi', 'START başarısız');
            if (ok) { curVal = true; window._cpopupSetToggle(btnStart, btnStop, true); }
          };
          btnStop.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnStop,
              () => window._cpopupWriteBool('${startTag}', false, null, curVal, '${displayName.replace(/'/g, "\\'")}'),
              '${startTag}', false, 'STOP komutu gönderildi', 'STOP başarısız');
            if (ok) { curVal = false; window._cpopupSetToggle(btnStart, btnStop, false); }
          };
        })();
      </script>
    `;
  });

  // ============================================================
  // POPUP TİPİ: control-onoff-auto
  // Modlu motor — Auto/Man + Start/Stop (suffix: _Au, _Mstr)
  // ============================================================

  registerPopupType('control-onoff-auto', async (config) => {
    const root = config.rootTag;
    const sfx = config.suffixes || {};
    const autoTag = root + (sfx.auto || '_Au');
    const startTag = root + (sfx.start || '_Mstr');
    const labels = config.labels || {};
    const displayName = config.title || config.displayName || root;

    let isAuto = null, isRunning = null;
    const autoRaw = await readTagValue(autoTag);
    if (autoRaw !== null) isAuto = autoRaw === true || autoRaw === 'true' || autoRaw === 1;
    const startRaw = await readTagValue(startTag);
    if (startRaw !== null) isRunning = startRaw === true || startRaw === 'true' || startRaw === 1;

    return `
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-auto ${isAuto === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-auto-btn">
          <i class="fas fa-robot"></i> ${escapeHtml(labels.autoBtn || 'AUTO')}
        </button>
        <button class="cpopup-btn cpopup-btn-man ${isAuto === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-man-btn">
          <i class="fas fa-hand-paper"></i> ${escapeHtml(labels.manBtn || 'MAN')}
        </button>
      </div>
      <div class="cpopup-divider"></div>
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-start ${isRunning === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-start-btn">
          <i class="fas fa-play"></i> ${escapeHtml(labels.startBtn || 'START')}
        </button>
        <button class="cpopup-btn cpopup-btn-stop ${isRunning === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-stop-btn">
          <i class="fas fa-stop"></i> ${escapeHtml(labels.stopBtn || 'STOP')}
        </button>
      </div>
      <script>
        (function() {
          const btnAuto = document.getElementById('cpopup-auto-btn');
          const btnMan = document.getElementById('cpopup-man-btn');
          const btnStart = document.getElementById('cpopup-start-btn');
          const btnStop = document.getElementById('cpopup-stop-btn');
          let curAuto = ${isAuto === true};
          let curRun = ${isRunning === true};
          btnAuto.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnAuto,
              () => window._cpopupWriteBool('${autoTag}', true, null, curAuto, '${displayName.replace(/'/g, "\\'")}'),
              '${autoTag}', true, 'AUTO moda geçildi', 'AUTO mod başarısız');
            if (ok) { curAuto = true; window._cpopupSetToggle(btnAuto, btnMan, true); }
          };
          btnMan.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnMan,
              () => window._cpopupWriteBool('${autoTag}', false, null, curAuto, '${displayName.replace(/'/g, "\\'")}'),
              '${autoTag}', false, 'MANUEL moda geçildi', 'MANUEL mod başarısız');
            if (ok) { curAuto = false; window._cpopupSetToggle(btnAuto, btnMan, false); }
          };
          btnStart.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnStart,
              () => window._cpopupWriteBool('${startTag}', true, null, curRun, '${displayName.replace(/'/g, "\\'")}'),
              '${startTag}', true, 'START komutu gönderildi', 'START başarısız');
            if (ok) { curRun = true; window._cpopupSetToggle(btnStart, btnStop, true); }
          };
          btnStop.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnStop,
              () => window._cpopupWriteBool('${startTag}', false, null, curRun, '${displayName.replace(/'/g, "\\'")}'),
              '${startTag}', false, 'STOP komutu gönderildi', 'STOP başarısız');
            if (ok) { curRun = false; window._cpopupSetToggle(btnStart, btnStop, false); }
          };
        })();
      </script>
    `;
  });

  // ============================================================
  // POPUP TİPİ: control-onf3
  // ONF3 — Start/Stop + Man SP
  // Popup açılırken iki tag birlikte okunur; UYGULA yalnız değişenleri yazar.
  // ============================================================

  const buildOnf3Popup = async (config) => {
    const root = config.rootTag;
    const sfx = config.suffixes || {};
    const startTag = root + (sfx.start || '_Mstr');
    const manSPTag = root + (sfx.manSP || '_Mval');
    const resetTag = root + (sfx.reset || '_Res');
    const labels = config.labels || {};
    const displayName = config.title || config.displayName || root;

    let startValue = null;
    let manSPValue = null;
    try {
      const result = await plcReadTags([startTag, manSPTag]);
      const values = result && result.tags ? result.tags : {};
      const unwrap = value => value && typeof value === 'object' ? value.value : value;
      if (values[startTag] !== undefined) startValue = unwrap(values[startTag]);
      if (values[manSPTag] !== undefined) manSPValue = unwrap(values[manSPTag]);
    } catch (e) {
      // readTagValue dashboard cache'ine de bakar; toplu okuma başarısızsa
      // mevcut canlı değerlerle popup'ı yine açabilmek için fallback kullan.
      startValue = await readTagValue(startTag);
      manSPValue = await readTagValue(manSPTag);
    }

    const toBool = value => {
      if (value === null || value === undefined || value === '') return null;
      return value === true || value === 'true' || value === 1 || value === '1';
    };
    const initialStart = toBool(startValue);
    const initialManSP = manSPValue === null || manSPValue === undefined || manSPValue === ''
      ? null
      : Number(manSPValue);
    const safeInitialManSP = Number.isFinite(initialManSP) ? initialManSP : null;
    const inlineData = JSON.stringify({
      startTag,
      manSPTag,
      resetTag,
      displayName,
      initialStart,
      initialManSP: safeInitialManSP
    }).replace(/</g, '\\u003c');

    return `
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-start ${initialStart === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onf3-start">
          <i class="fas fa-play"></i> ${escapeHtml(labels.startBtn || 'START')}
        </button>
        <button class="cpopup-btn cpopup-btn-stop ${initialStart === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onf3-stop">
          <i class="fas fa-stop"></i> ${escapeHtml(labels.stopBtn || 'STOP')}
        </button>
      </div>
      <div class="cpopup-input-group">
        <label>${escapeHtml(labels.manSPLabel || 'Man SP')}</label>
        <input type="number" id="cpopup-onf3-mansp" value="${safeInitialManSP === null ? '' : escapeHtml(String(safeInitialManSP))}" step="any" />
      </div>
      <div class="cpopup-footer">
        <button class="cpopup-btn cpopup-btn-reset" id="cpopup-onf3-reset">
          <i class="fas fa-undo"></i> ${escapeHtml(labels.resetBtn || 'RESET')}
        </button>
        <button class="cpopup-btn cpopup-btn-apply" id="cpopup-onf3-apply">
          <i class="fas fa-check"></i> ${escapeHtml(labels.applyBtn || 'UYGULA')}
        </button>
      </div>
      <script>
        (function() {
          const data = ${inlineData};
          const btnStart = document.getElementById('cpopup-onf3-start');
          const btnStop = document.getElementById('cpopup-onf3-stop');
          const btnReset = document.getElementById('cpopup-onf3-reset');
          const btnApply = document.getElementById('cpopup-onf3-apply');
          const manSPInput = document.getElementById('cpopup-onf3-mansp');
          let initialStart = data.initialStart;
          let initialManSP = data.initialManSP;
          let selectedStart = initialStart;

          function renderSelections() {
            if (selectedStart !== null) window._cpopupSetToggle(btnStart, btnStop, selectedStart);
          }

          btnStart.onclick = () => { selectedStart = true; renderSelections(); };
          btnStop.onclick = () => { selectedStart = false; renderSelections(); };

          btnReset.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnReset,
              () => window._cpopupWriteBool(data.resetTag, true, null, false, data.displayName, { verificationMode: 'acknowledgement', source: 'control-popup-reset' }),
              null, null, 'RESET komutu gönderildi', 'RESET başarısız');
            if (ok) window._cpopupManager.close();
          };

          btnApply.onclick = async () => {
            const rawManSP = manSPInput.value.trim();
            const nextManSP = rawManSP === '' ? null : Number(rawManSP);
            if (rawManSP !== '' && !Number.isFinite(nextManSP)) {
              window._cpopupNotify('Geçerli bir Man SP değeri giriniz', 'warning');
              manSPInput.focus();
              return;
            }
            if (rawManSP === '' && initialManSP !== null) {
              window._cpopupNotify('Man SP değeri boş bırakılamaz', 'warning');
              manSPInput.focus();
              return;
            }

            const writes = [];
            if (selectedStart !== null && selectedStart !== initialStart) {
              writes.push({ tag_name: data.startTag, value: selectedStart, data_type: 'Bool', old_value: initialStart, display_name: data.displayName });
            }
            if (nextManSP !== null && (initialManSP === null || nextManSP !== initialManSP)) {
              writes.push({ tag_name: data.manSPTag, value: nextManSP, data_type: 'Real', old_value: initialManSP, display_name: data.displayName });
            }

            if (writes.length === 0) {
              window._cpopupNotify('Herhangi bir değişiklik yapılmadı', 'info');
              return;
            }

            const confirmed = await window._cpopupConfirm(writes.length + ' değişiklik PLC\\'ye gönderilecek. Onaylıyor musunuz?');
            if (!confirmed) return;

            const originalHtml = btnApply.innerHTML;
            btnApply.disabled = true;
            btnReset.disabled = true;
            btnApply.innerHTML = '<span class="cpopup-spinner"></span>';
            try {
              const result = await window._cpopupWriteBatch(writes);
              const succeeded = new Set((result && result.results ? result.results : [])
                .filter(item => item.success)
                .map(item => item.tag_name));
              writes.forEach(write => {
                if (result && result.success) succeeded.add(write.tag_name);
              });
              if (succeeded.has(data.startTag)) initialStart = selectedStart;
              if (succeeded.has(data.manSPTag)) initialManSP = nextManSP;

              if (window._cpopupNotifyBatchResult(result, writes.length + ' değişiklik uygulandı ve geri okumadan doğrulandı', 'Değişiklikler uygulanamadı')) {
                window._cpopupManager.close();
                return;
              }

              console.warn('[ONF3] PLC batch write failed after retry:', result);
            } catch (error) {
              window._cpopupNotify('ONF3 değişiklikleri gönderilemedi', 'error');
            } finally {
              btnApply.innerHTML = originalHtml;
              btnApply.disabled = false;
              btnReset.disabled = false;
            }
          };
        })();
      </script>
    `;
  };

  registerPopupType('control-onf3', buildOnf3Popup);
  // Eski/harici kayıtlarda doğrudan "ONF3" kullanılmışsa da açılabilsin.
  registerPopupType('onf3', buildOnf3Popup);

  // ============================================================
  // POPUP TİPİ: control-setpoint
  // Tek set değeri — Input + Uygula (suffix: _Aval)
  // ============================================================

  registerPopupType('control-setpoint', async (config) => {
    const root = config.rootTag;
    const sfx = config.suffixes || {};
    const setTag = root + (sfx.autoSP || '_Aval');
    const labels = config.labels || {};
    const displayName = config.title || config.displayName || root;

    let currentValue = await readTagValue(setTag);
    if (currentValue === null) currentValue = '';

    return `
      <div class="cpopup-input-group">
        <label>${escapeHtml(labels.inputLabel || 'Set Point')}</label>
        <input type="number" id="cpopup-sp-input" value="${currentValue}" step="any" />
      </div>
      <div class="cpopup-footer">
        <button class="cpopup-btn cpopup-btn-apply" id="cpopup-sp-apply">
          <i class="fas fa-check"></i> ${escapeHtml(labels.applyBtn || 'UYGULA')}
        </button>
      </div>
      <script>
        (function() {
          const initVal = '${currentValue}';
          document.getElementById('cpopup-sp-apply').onclick = async () => {
            const val = document.getElementById('cpopup-sp-input').value;
            if (val === '' || isNaN(val)) { window._cpopupNotify('Geçerli bir sayı giriniz', 'warning'); return; }
            if (val === initVal) { window._cpopupNotify('Değer değişmedi', 'info'); return; }
            const btn = document.getElementById('cpopup-sp-apply');
            const ok = await window._cpopupWriteAndVerify(btn,
              () => window._cpopupWriteReal('${setTag}', val, null, initVal !== '' ? parseFloat(initVal) : null, '${displayName.replace(/'/g, "\\'")}'),
              '${setTag}', Number(val), 'Set değeri uygulandı: ' + val, 'Set değeri gönderilemedi');
            if (ok) window._cpopupManager.close();
          };
        })();
      </script>
    `;
  });

  // ============================================================
  // POPUP TİPİ: control-proportional
  // Tam oransal kontrol — Auto/Man, Start/Stop, AutoSP, ManSP, Reset
  // ============================================================

  registerPopupType('control-proportional', async (config) => {
    const root = config.rootTag;
    const sfx = config.suffixes || {};
    const autoTag = root + (sfx.auto || '_Au');
    const startTag = root + (sfx.start || '_Mstr');
    const autoSPTag = root + (sfx.autoSP || '_Aval');
    const manSPTag = root + (sfx.manSP || '_Mval');
    const resetTag = root + (sfx.reset || '_Res');
    const labels = config.labels || {};
    const displayName = config.title || config.displayName || root;

    let isAuto = null, isRunning = null, autoSP = '', manSP = '';
    const autoRaw = await readTagValue(autoTag);
    if (autoRaw !== null) isAuto = autoRaw === true || autoRaw === 'true' || autoRaw === 1;
    const startRaw = await readTagValue(startTag);
    if (startRaw !== null) isRunning = startRaw === true || startRaw === 'true' || startRaw === 1;
    const aspRaw = await readTagValue(autoSPTag);
    if (aspRaw !== null) autoSP = aspRaw;
    const mspRaw = await readTagValue(manSPTag);
    if (mspRaw !== null) manSP = mspRaw;

    return `
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-auto ${isAuto === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-pr-auto">
          <i class="fas fa-robot"></i> ${escapeHtml(labels.autoBtn || 'AUTO')}
        </button>
        <button class="cpopup-btn cpopup-btn-man ${isAuto === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-pr-man">
          <i class="fas fa-hand-paper"></i> ${escapeHtml(labels.manBtn || 'MAN')}
        </button>
      </div>
      <div class="cpopup-divider"></div>
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-start ${isRunning === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-pr-start">
          <i class="fas fa-play"></i> ${escapeHtml(labels.startBtn || 'START')}
        </button>
        <button class="cpopup-btn cpopup-btn-stop ${isRunning === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-pr-stop">
          <i class="fas fa-stop"></i> ${escapeHtml(labels.stopBtn || 'STOP')}
        </button>
      </div>
      <div class="cpopup-divider"></div>
      <div style="display:flex; gap:10px;">
        <div class="cpopup-input-group" style="flex:1">
          <label>Auto SP</label>
          <input type="number" id="cpopup-pr-autosp" value="${autoSP}" step="any" />
        </div>
        <div class="cpopup-input-group" style="flex:1">
          <label>Man SP</label>
          <input type="number" id="cpopup-pr-mansp" value="${manSP}" step="any" />
        </div>
      </div>
      <div class="cpopup-footer">
        <button class="cpopup-btn cpopup-btn-reset" id="cpopup-pr-reset">
          <i class="fas fa-undo"></i> ${escapeHtml(labels.resetBtn || 'RESET')}
        </button>
        <button class="cpopup-btn cpopup-btn-apply" id="cpopup-pr-apply">
          <i class="fas fa-check"></i> ${escapeHtml(labels.applyBtn || 'UYGULA')}
        </button>
      </div>
      <script>
        (function() {
          const initAutoSP = '${autoSP}';
          const initManSP = '${manSP}';
          const btnAuto = document.getElementById('cpopup-pr-auto');
          const btnMan = document.getElementById('cpopup-pr-man');
          const btnStart = document.getElementById('cpopup-pr-start');
          const btnStop = document.getElementById('cpopup-pr-stop');
          let curAuto = ${isAuto === true};
          let curRun = ${isRunning === true};
          const dn = '${displayName.replace(/'/g, "\\'")}';

          btnAuto.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnAuto,
              () => window._cpopupWriteBool('${autoTag}', true, null, curAuto, dn),
              '${autoTag}', true, 'AUTO moda geçildi', 'AUTO mod başarısız');
            if (ok) { curAuto = true; window._cpopupSetToggle(btnAuto, btnMan, true); }
          };
          btnMan.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnMan,
              () => window._cpopupWriteBool('${autoTag}', false, null, curAuto, dn),
              '${autoTag}', false, 'MANUEL moda geçildi', 'MANUEL mod başarısız');
            if (ok) { curAuto = false; window._cpopupSetToggle(btnAuto, btnMan, false); }
          };
          btnStart.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnStart,
              () => window._cpopupWriteBool('${startTag}', true, null, curRun, dn),
              '${startTag}', true, 'START komutu gönderildi', 'START başarısız');
            if (ok) { curRun = true; window._cpopupSetToggle(btnStart, btnStop, true); }
          };
          btnStop.onclick = async () => {
            const ok = await window._cpopupWriteAndVerify(btnStop,
              () => window._cpopupWriteBool('${startTag}', false, null, curRun, dn),
              '${startTag}', false, 'STOP komutu gönderildi', 'STOP başarısız');
            if (ok) { curRun = false; window._cpopupSetToggle(btnStart, btnStop, false); }
          };

          document.getElementById('cpopup-pr-reset').onclick = async () => {
            const btn = document.getElementById('cpopup-pr-reset');
            const ok = await window._cpopupWriteAndVerify(btn,
              () => window._cpopupWriteBool('${resetTag}', true, null, false, dn, { verificationMode: 'acknowledgement', source: 'control-popup-reset' }),
              null, null, 'RESET komutu gönderildi', 'RESET başarısız');
          };

          document.getElementById('cpopup-pr-apply').onclick = async () => {
            const newAutoSP = document.getElementById('cpopup-pr-autosp').value;
            const newManSP = document.getElementById('cpopup-pr-mansp').value;
            const writes = [];
            if (newAutoSP !== initAutoSP && newAutoSP !== '') {
              writes.push({ tag_name: '${autoSPTag}', value: Number(newAutoSP), data_type: 'Real', old_value: initAutoSP !== '' ? parseFloat(initAutoSP) : null, display_name: dn });
            }
            if (newManSP !== initManSP && newManSP !== '') {
              writes.push({ tag_name: '${manSPTag}', value: Number(newManSP), data_type: 'Real', old_value: initManSP !== '' ? parseFloat(initManSP) : null, display_name: dn });
            }
            if (!writes.length) {
              window._cpopupNotify('Herhangi bir değer değişikliği yapılmadı', 'info');
              return;
            }
            const button = document.getElementById('cpopup-pr-apply');
            const originalHtml = button.innerHTML;
            button.disabled = true;
            button.innerHTML = '<span class="cpopup-spinner"></span><span>Geri okuma doğrulanıyor...</span>';
            try {
              const result = await window._cpopupWriteBatch(writes);
              if (window._cpopupNotifyBatchResult(result, 'Set değerleri uygulandı ve geri okumadan doğrulandı', 'Set değerleri uygulanamadı')) {
                window._cpopupManager.close();
              }
            } catch (error) {
              window._cpopupNotify('Set değerleri uygulanamadı', 'error');
            } finally {
              button.innerHTML = originalHtml;
              button.disabled = false;
            }
          };
        })();
      </script>
    `;
  });

  // ============================================================
  // SABİT LEGACY ORANSAL PROFİLLER: OR2, OR3, OR5, OR6
  // Dışarıdan popup kimlikleri sabittir; ortak renderer yalnız kod tekrarını azaltır.
  // ============================================================

  async function buildLegacyOrPopup(config, profile) {
    const root = config.rootTag;
    const sfx = config.suffixes || {};
    const labels = config.labels || {};
    const displayName = config.title || config.displayName || root;
    const modeTag = root + (sfx[profile.modeKey] || profile.modeSuffix);
    const startTag = profile.start ? root + (sfx.start || '_Mstr') : null;
    const autoSPTag = profile.autoSP ? root + (sfx.autoSP || '_Aval') : null;
    const manSPTag = profile.manSP ? root + (sfx.manSP || '_Mval') : null;
    const resetTag = root + (sfx.reset || '_Res');
    const readTags = [modeTag, startTag, autoSPTag, manSPTag].filter(Boolean);
    const values = await readPopupTagValues(readTags);
    const modeRaw = toBooleanValue(values[modeTag]);
    const initialAuto = modeRaw === null ? null : modeRaw === profile.autoModeValue;
    const initialStart = startTag ? toBooleanValue(values[startTag]) : null;
    const initialAutoSP = autoSPTag ? toFiniteNumber(values[autoSPTag]) : null;
    const initialManSP = manSPTag ? toFiniteNumber(values[manSPTag]) : null;
    const id = profile.id;
    const data = serializeInlineData({
      modeTag, startTag, autoSPTag, manSPTag, resetTag, displayName,
      autoModeValue: profile.autoModeValue,
      initialAuto, initialStart, initialAutoSP, initialManSP
    });

    return `
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-auto ${initialAuto === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-${id}-auto">
          <i class="fas fa-robot"></i> ${escapeHtml(labels.autoBtn || 'AUTO')}
        </button>
        <button class="cpopup-btn cpopup-btn-man ${initialAuto === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-${id}-man">
          <i class="fas fa-hand-paper"></i> ${escapeHtml(labels.manBtn || 'MAN')}
        </button>
      </div>
      ${startTag ? `
      <div class="cpopup-divider"></div>
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-start ${initialStart === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-${id}-start">
          <i class="fas fa-play"></i> ${escapeHtml(labels.startBtn || 'START')}
        </button>
        <button class="cpopup-btn cpopup-btn-stop ${initialStart === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-${id}-stop">
          <i class="fas fa-stop"></i> ${escapeHtml(labels.stopBtn || 'STOP')}
        </button>
      </div>` : ''}
      ${(autoSPTag || manSPTag) ? `
      <div class="cpopup-divider"></div>
      <div style="display:flex; gap:10px;">
        ${autoSPTag ? `<div class="cpopup-input-group" style="flex:1"><label>${escapeHtml(labels.autoSPLabel || 'Auto SP')}</label><input type="number" id="cpopup-${id}-autosp" value="${initialAutoSP === null ? '' : initialAutoSP}" step="any" /></div>` : ''}
        ${manSPTag ? `<div class="cpopup-input-group" style="flex:1"><label>${escapeHtml(labels.manSPLabel || 'Man SP')}</label><input type="number" id="cpopup-${id}-mansp" value="${initialManSP === null ? '' : initialManSP}" step="any" /></div>` : ''}
      </div>` : ''}
      <div class="cpopup-footer">
        <button class="cpopup-btn cpopup-btn-reset" id="cpopup-${id}-reset"><i class="fas fa-undo"></i> ${escapeHtml(labels.resetBtn || 'RESET')}</button>
        ${(autoSPTag || manSPTag) ? `<button class="cpopup-btn cpopup-btn-apply" id="cpopup-${id}-apply"><i class="fas fa-check"></i> ${escapeHtml(labels.applyBtn || 'UYGULA')}</button>` : ''}
      </div>
      <script>
        (function() {
          const data = ${data};
          const btnAuto = document.getElementById('cpopup-${id}-auto');
          const btnMan = document.getElementById('cpopup-${id}-man');
          let currentAuto = data.initialAuto;

          async function setMode(autoSelected, button) {
            const rawValue = autoSelected ? data.autoModeValue : !data.autoModeValue;
            const oldRawValue = currentAuto === null ? null : (currentAuto ? data.autoModeValue : !data.autoModeValue);
            const ok = await window._cpopupWriteAndVerify(button,
              () => window._cpopupWriteBool(data.modeTag, rawValue, null, oldRawValue, data.displayName),
              data.modeTag, rawValue, autoSelected ? 'AUTO moda geçildi' : 'MANUEL moda geçildi', 'Mod değiştirilemedi');
            if (ok) { currentAuto = autoSelected; window._cpopupSetToggle(btnAuto, btnMan, autoSelected); }
          }
          btnAuto.onclick = () => setMode(true, btnAuto);
          btnMan.onclick = () => setMode(false, btnMan);

          if (data.startTag) {
            const btnStart = document.getElementById('cpopup-${id}-start');
            const btnStop = document.getElementById('cpopup-${id}-stop');
            let currentStart = data.initialStart;
            btnStart.onclick = async () => {
              const ok = await window._cpopupWriteAndVerify(btnStart,
                () => window._cpopupWriteBool(data.startTag, true, null, currentStart, data.displayName),
                data.startTag, true, 'START komutu gönderildi', 'START başarısız');
              if (ok) { currentStart = true; window._cpopupSetToggle(btnStart, btnStop, true); }
            };
            btnStop.onclick = async () => {
              const ok = await window._cpopupWriteAndVerify(btnStop,
                () => window._cpopupWriteBool(data.startTag, false, null, currentStart, data.displayName),
                data.startTag, false, 'STOP komutu gönderildi', 'STOP başarısız');
              if (ok) { currentStart = false; window._cpopupSetToggle(btnStart, btnStop, false); }
            };
          }

          document.getElementById('cpopup-${id}-reset').onclick = async function() {
            const ok = await window._cpopupWriteAndVerify(this,
              () => window._cpopupWriteBool(data.resetTag, true, null, false, data.displayName, { verificationMode: 'acknowledgement', source: 'control-popup-reset' }),
              null, null, 'RESET komutu gönderildi', 'RESET başarısız');
            if (ok) window._cpopupManager.close();
          };

          const applyButton = document.getElementById('cpopup-${id}-apply');
          if (applyButton) applyButton.onclick = async () => {
            const writes = [];
            if (data.autoSPTag) {
              const raw = document.getElementById('cpopup-${id}-autosp').value.trim();
              const value = raw === '' ? null : Number(raw);
              if (raw !== '' && !Number.isFinite(value)) { window._cpopupNotify('Geçerli bir Auto SP değeri giriniz', 'warning'); return; }
              if (value !== null && value !== data.initialAutoSP) writes.push({ tag_name: data.autoSPTag, value, data_type: 'Real', old_value: data.initialAutoSP, display_name: data.displayName });
            }
            if (data.manSPTag) {
              const raw = document.getElementById('cpopup-${id}-mansp').value.trim();
              const value = raw === '' ? null : Number(raw);
              if (raw !== '' && !Number.isFinite(value)) { window._cpopupNotify('Geçerli bir Man SP değeri giriniz', 'warning'); return; }
              if (value !== null && value !== data.initialManSP) writes.push({ tag_name: data.manSPTag, value, data_type: 'Real', old_value: data.initialManSP, display_name: data.displayName });
            }
            if (!writes.length) { window._cpopupNotify('Herhangi bir değişiklik yapılmadı', 'info'); return; }
            applyButton.disabled = true;
            const originalHtml = applyButton.innerHTML;
            applyButton.innerHTML = '<span class="cpopup-spinner"></span><span>Geri okuma doğrulanıyor...</span>';
            try {
              const result = await window._cpopupWriteBatch(writes);
              if (window._cpopupNotifyBatchResult(result, 'Set değerleri uygulandı ve geri okumadan doğrulandı', 'Set değerleri uygulanamadı')) window._cpopupManager.close();
            } catch (error) { window._cpopupNotify('Set değerleri gönderilemedi', 'error'); }
            finally { applyButton.innerHTML = originalHtml; applyButton.disabled = false; }
          };
        })();
      </script>
    `;
  }

  const LEGACY_OR_PROFILES = Object.freeze({
    or2: { id: 'or2', modeKey: 'auto', modeSuffix: '_Au', autoModeValue: true, autoSP: true, manSP: true },
    or3: { id: 'or3', modeKey: 'auto', modeSuffix: '_Au', autoModeValue: true, manSP: true },
    or5: { id: 'or5', modeKey: 'mode', modeSuffix: '_Man', autoModeValue: false, start: true, autoSP: true, manSP: true },
    or6: { id: 'or6', modeKey: 'auto', modeSuffix: '_Au', autoModeValue: true, autoSP: true, manSP: true }
  });
  Object.entries(LEGACY_OR_PROFILES).forEach(([legacyName, profile]) => {
    const builder = config => buildLegacyOrPopup(config, profile);
    registerPopupType('control-' + legacyName, builder);
    registerPopupType(legacyName, builder);
  });

  // ============================================================
  // POPUP TİPİ: control-cav
  // CAV — Auto SP yazılır, Vmin/Vmax yalnız bilgi olarak gösterilir.
  // ============================================================

  const buildCavPopup = async (config) => {
    const root = config.rootTag;
    const sfx = config.suffixes || {};
    const setTag = root + (sfx.autoSP || '_Aval');
    const minTag = root + (sfx.vmin || '_Vmin');
    const maxTag = root + (sfx.vmax || '_Vmax');
    const displayName = config.title || config.displayName || root;
    const values = await readPopupTagValues([setTag, minTag, maxTag]);
    const data = serializeInlineData({
      setTag, displayName,
      initialValue: toFiniteNumber(values[setTag])
    });
    const minValue = toFiniteNumber(values[minTag]);
    const maxValue = toFiniteNumber(values[maxTag]);
    return `
      <div class="cpopup-input-group">
        <label>${escapeHtml((config.labels || {}).autoSPLabel || 'Auto SP')}</label>
        <input type="number" id="cpopup-cav-set" value="${toFiniteNumber(values[setTag]) === null ? '' : toFiniteNumber(values[setTag])}" step="any" />
      </div>
      <div style="display:flex;justify-content:space-between;color:#8a8ea8;font-size:12px;margin-top:-6px;">
        <span>Vmin: ${minValue === null ? '-' : escapeHtml(String(minValue))}</span><span>Vmax: ${maxValue === null ? '-' : escapeHtml(String(maxValue))}</span>
      </div>
      <div class="cpopup-footer"><button class="cpopup-btn cpopup-btn-apply" id="cpopup-cav-apply"><i class="fas fa-check"></i> UYGULA</button></div>
      <script>
        (function() {
          const data = ${data};
          const button = document.getElementById('cpopup-cav-apply');
          button.onclick = async () => {
            const raw = document.getElementById('cpopup-cav-set').value.trim();
            const value = raw === '' ? null : Number(raw);
            if (value === null || !Number.isFinite(value)) { window._cpopupNotify('Geçerli bir Auto SP değeri giriniz', 'warning'); return; }
            if (value === data.initialValue) { window._cpopupNotify('Değer değişmedi', 'info'); return; }
            const ok = await window._cpopupWriteAndVerify(button,
              () => window._cpopupWriteReal(data.setTag, value, null, data.initialValue, data.displayName),
              data.setTag, value, 'Auto SP uygulandı', 'Auto SP gönderilemedi');
            if (ok) window._cpopupManager.close();
          };
        })();
      </script>
    `;
  };
  registerPopupType('control-cav', buildCavPopup);
  registerPopupType('cav', buildCavPopup);

  // ============================================================
  // POPUP TİPİ: control-onf4
  // İki hızlı motor: _Mstr ve _Mstr2 birbirini dışlar.
  // ============================================================

  const buildOnf4Popup = async (config) => {
    const root = config.rootTag;
    const sfx = config.suffixes || {};
    const startTag = root + (sfx.start || '_Mstr');
    const start2Tag = root + (sfx.start2 || '_Mstr2');
    const displayName = config.title || config.displayName || root;
    const values = await readPopupTagValues([startTag, start2Tag]);
    const data = serializeInlineData({ startTag, start2Tag, displayName, speed1: toBooleanValue(values[startTag]), speed2: toBooleanValue(values[start2Tag]) });
    return `
      <div class="cpopup-btn-row" style="flex-wrap:wrap;">
        <button class="cpopup-btn cpopup-btn-start ${toBooleanValue(values[startTag]) === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onf4-speed1"><i class="fas fa-play"></i> HIZ 1</button>
        <button class="cpopup-btn cpopup-btn-auto ${toBooleanValue(values[start2Tag]) === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onf4-speed2"><i class="fas fa-forward"></i> HIZ 2</button>
        <button class="cpopup-btn cpopup-btn-stop ${(toBooleanValue(values[startTag]) === false && toBooleanValue(values[start2Tag]) === false) ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onf4-stop"><i class="fas fa-stop"></i> STOP</button>
      </div>
      <script>
        (function() {
          const data = ${data};
          const buttons = [document.getElementById('cpopup-onf4-speed1'), document.getElementById('cpopup-onf4-speed2'), document.getElementById('cpopup-onf4-stop')];
          function render(activeIndex) { buttons.forEach((button, index) => { button.classList.toggle('active', index === activeIndex); button.classList.toggle('cpopup-btn-inactive', index !== activeIndex); }); }
          async function send(writes, activeIndex) {
            const result = await window._cpopupWriteBatch(writes.map(write => ({ ...write, data_type: 'Bool', display_name: data.displayName })));
            if (window._cpopupNotifyBatchResult(result, 'Motor komutu uygulandı ve geri okumadan doğrulandı', 'Motor komutu uygulanamadı')) render(activeIndex);
          }
          buttons[0].onclick = () => send([{ tag_name: data.start2Tag, value: false }, { tag_name: data.startTag, value: true }], 0);
          buttons[1].onclick = () => send([{ tag_name: data.startTag, value: false }, { tag_name: data.start2Tag, value: true }], 1);
          buttons[2].onclick = () => send([{ tag_name: data.startTag, value: false }, { tag_name: data.start2Tag, value: false }], 2);
        })();
      </script>
    `;
  };
  registerPopupType('control-onf4', buildOnf4Popup);
  registerPopupType('onf4', buildOnf4Popup);

  // ============================================================
  // POPUP TİPİ: control-onf5
  // Güneş ışığı kontrollü motor: mod, komut tagları, çalışma durumu ve checkbox.
  // ============================================================

  const buildOnf5Popup = async (config) => {
    const root = config.rootTag;
    const sfx = config.suffixes || {};
    const tags = {
      auto: root + (sfx.auto || '_Au'), start: root + (sfx.start || '_Mstr'), stop: root + (sfx.stop || '_Mstp'),
      running: root + (sfx.running || '_Runng'), sunLight: root + (sfx.sunLight || '_SunLightEnable')
    };
    const values = await readPopupTagValues([tags.auto, tags.running, tags.sunLight]);
    const data = serializeInlineData({ ...tags, displayName: config.title || config.displayName || root, isAuto: toBooleanValue(values[tags.auto]), isRunning: toBooleanValue(values[tags.running]), sunLightEnabled: toBooleanValue(values[tags.sunLight]) });
    return `
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-auto ${toBooleanValue(values[tags.auto]) === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onf5-auto">AUTO</button>
        <button class="cpopup-btn cpopup-btn-man ${toBooleanValue(values[tags.auto]) === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onf5-man">MAN</button>
      </div>
      <div class="cpopup-divider"></div>
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-start ${toBooleanValue(values[tags.running]) === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onf5-start">START</button>
        <button class="cpopup-btn cpopup-btn-stop ${toBooleanValue(values[tags.running]) === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onf5-stop">STOP</button>
      </div>
      <label style="display:flex;align-items:center;gap:8px;margin-top:14px;"><input type="checkbox" id="cpopup-onf5-sunlight" ${toBooleanValue(values[tags.sunLight]) === true ? 'checked' : ''}> SunLight Enable</label>
      <script>
        (function() {
          const data = ${data};
          const autoButton = document.getElementById('cpopup-onf5-auto');
          const manButton = document.getElementById('cpopup-onf5-man');
          const startButton = document.getElementById('cpopup-onf5-start');
          const stopButton = document.getElementById('cpopup-onf5-stop');
          let isAuto = data.isAuto;
          async function setBool(button, tag, value, oldValue, successText) {
            return window._cpopupWriteAndVerify(button, () => window._cpopupWriteBool(tag, value, null, oldValue, data.displayName), tag, value, successText, 'Komut gönderilemedi');
          }
          autoButton.onclick = async () => { if (await setBool(autoButton, data.auto, true, isAuto, 'AUTO moda geçildi')) { isAuto = true; window._cpopupSetToggle(autoButton, manButton, true); } };
          manButton.onclick = async () => { if (await setBool(manButton, data.auto, false, isAuto, 'MANUEL moda geçildi')) { isAuto = false; window._cpopupSetToggle(autoButton, manButton, false); } };
          startButton.onclick = async () => { if (await setBool(startButton, data.start, true, false, 'START komutu gönderildi')) window._cpopupSetToggle(startButton, stopButton, true); };
          stopButton.onclick = async () => { if (await setBool(stopButton, data.stop, true, false, 'STOP komutu gönderildi')) window._cpopupSetToggle(startButton, stopButton, false); };
          document.getElementById('cpopup-onf5-sunlight').onchange = async function() {
            const previous = !this.checked;
            if (!await setBool(this, data.sunLight, this.checked, previous, 'SunLight ayarı gönderildi')) this.checked = previous;
          };
        })();
      </script>
    `;
  };
  registerPopupType('control-onf5', buildOnf5Popup);
  registerPopupType('onf5', buildOnf5Popup);

  // ============================================================
  // POPUP TİPİ: control-onf6
  // Radyan/FanCoil: mod, start/stop, üç set değeri ve periyodik çalışma.
  // ============================================================

  const buildOnf6Popup = async (config) => {
    const root = config.rootTag;
    const sfx = config.suffixes || {};
    const tags = {
      auto: root + (sfx.auto || '_Au'), start: root + (sfx.start || '_Mstr'), running: root + (sfx.running || '_Runng'),
      tempSet: root + (sfx.tempSet || '_TempSet'), runTimeSet: root + (sfx.runTimeSet || '_RunTimeMinuteSet'),
      waitTimeSet: root + (sfx.waitTimeSet || '_WaitTimeMinuteSet'), periodic: root + (sfx.periodic || '_PeriodicWorkEnable'),
      reset: root + (sfx.reset || '_Res')
    };
    const values = await readPopupTagValues([tags.tempSet, tags.runTimeSet, tags.waitTimeSet, tags.auto, tags.running, tags.periodic]);
    const data = serializeInlineData({
      ...tags, displayName: config.title || config.displayName || root,
      isAuto: toBooleanValue(values[tags.auto]), isRunning: toBooleanValue(values[tags.running]), periodicEnabled: toBooleanValue(values[tags.periodic]),
      tempValue: toFiniteNumber(values[tags.tempSet]), runTimeValue: toFiniteNumber(values[tags.runTimeSet]), waitTimeValue: toFiniteNumber(values[tags.waitTimeSet])
    });
    return `
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-auto ${toBooleanValue(values[tags.auto]) === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onf6-auto">AUTO</button>
        <button class="cpopup-btn cpopup-btn-man ${toBooleanValue(values[tags.auto]) === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onf6-man">MAN</button>
      </div>
      <div class="cpopup-divider"></div>
      <div class="cpopup-btn-row">
        <button class="cpopup-btn cpopup-btn-start ${toBooleanValue(values[tags.running]) === true ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onf6-start">START</button>
        <button class="cpopup-btn cpopup-btn-stop ${toBooleanValue(values[tags.running]) === false ? 'active' : 'cpopup-btn-inactive'}" id="cpopup-onf6-stop">STOP</button>
      </div>
      <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin-top:14px;">
        <div class="cpopup-input-group"><label>Sıcaklık Set</label><input type="number" id="cpopup-onf6-temp" value="${toFiniteNumber(values[tags.tempSet]) === null ? '' : toFiniteNumber(values[tags.tempSet])}" step="any"></div>
        <div class="cpopup-input-group"><label>Çalışma Set</label><input type="number" id="cpopup-onf6-run" value="${toFiniteNumber(values[tags.runTimeSet]) === null ? '' : toFiniteNumber(values[tags.runTimeSet])}" step="any"></div>
        <div class="cpopup-input-group"><label>Bekleme Set</label><input type="number" id="cpopup-onf6-wait" value="${toFiniteNumber(values[tags.waitTimeSet]) === null ? '' : toFiniteNumber(values[tags.waitTimeSet])}" step="any"></div>
      </div>
      <label style="display:flex;align-items:center;gap:8px;"><input type="checkbox" id="cpopup-onf6-periodic" ${toBooleanValue(values[tags.periodic]) === true ? 'checked' : ''}> Periyodik Çalışma</label>
      <div class="cpopup-footer">
        <button class="cpopup-btn cpopup-btn-reset" id="cpopup-onf6-reset">RESET</button>
        <button class="cpopup-btn cpopup-btn-apply" id="cpopup-onf6-apply">UYGULA</button>
      </div>
      <script>
        (function() {
          const data = ${data};
          const autoButton = document.getElementById('cpopup-onf6-auto');
          const manButton = document.getElementById('cpopup-onf6-man');
          const startButton = document.getElementById('cpopup-onf6-start');
          const stopButton = document.getElementById('cpopup-onf6-stop');
          let isAuto = data.isAuto;
          let isRunning = data.isRunning;
          async function setBool(button, tag, value, oldValue, successText) {
            return window._cpopupWriteAndVerify(button, () => window._cpopupWriteBool(tag, value, null, oldValue, data.displayName), tag, value, successText, 'Komut gönderilemedi');
          }
          autoButton.onclick = async () => { if (await setBool(autoButton, data.auto, true, isAuto, 'AUTO moda geçildi')) { isAuto = true; window._cpopupSetToggle(autoButton, manButton, true); } };
          manButton.onclick = async () => { if (await setBool(manButton, data.auto, false, isAuto, 'MANUEL moda geçildi')) { isAuto = false; window._cpopupSetToggle(autoButton, manButton, false); } };
          startButton.onclick = async () => { if (await setBool(startButton, data.start, true, isRunning, 'START komutu gönderildi')) { isRunning = true; window._cpopupSetToggle(startButton, stopButton, true); } };
          stopButton.onclick = async () => { if (await setBool(stopButton, data.start, false, isRunning, 'STOP komutu gönderildi')) { isRunning = false; window._cpopupSetToggle(startButton, stopButton, false); } };
          document.getElementById('cpopup-onf6-periodic').onchange = async function() {
            const previous = !this.checked;
            if (!await setBool(this, data.periodic, this.checked, previous, 'Periyodik çalışma ayarı gönderildi')) this.checked = previous;
          };
          document.getElementById('cpopup-onf6-reset').onclick = async function() {
            if (await setBool(this, data.reset, true, false, 'RESET komutu gönderildi')) window._cpopupManager.close();
          };
          document.getElementById('cpopup-onf6-apply').onclick = async function() {
            const definitions = [
              { id: 'cpopup-onf6-temp', tag: data.tempSet, initial: data.tempValue },
              { id: 'cpopup-onf6-run', tag: data.runTimeSet, initial: data.runTimeValue },
              { id: 'cpopup-onf6-wait', tag: data.waitTimeSet, initial: data.waitTimeValue }
            ];
            const writes = [];
            for (const definition of definitions) {
              const raw = document.getElementById(definition.id).value.trim();
              const value = raw === '' ? null : Number(raw);
              if (value === null || !Number.isFinite(value)) { window._cpopupNotify('Tüm set değerleri geçerli sayı olmalıdır', 'warning'); return; }
              if (value !== definition.initial) writes.push({ tag_name: definition.tag, value, data_type: 'Real', old_value: definition.initial, display_name: data.displayName });
            }
            if (!writes.length) { window._cpopupNotify('Herhangi bir değişiklik yapılmadı', 'info'); return; }
            const result = await window._cpopupWriteBatch(writes);
            if (window._cpopupNotifyBatchResult(result, 'Set değerleri uygulandı ve geri okumadan doğrulandı', 'Set değerleri uygulanamadı')) window._cpopupManager.close();
          };
        })();
      </script>
    `;
  };
  registerPopupType('control-onf6', buildOnf6Popup);
  registerPopupType('onf6', buildOnf6Popup);

  // ============================================================
  // POPUP TİPİ: sensor-chart
  // Sensör için sekmeli popup:
  //   Tab 1 — Trend: son 24h saatlik veri (Chart.js line chart)
  //   Tab 2 — Kalibrasyon: sensör kalibrasyon bilgileri ve PLC yazma formu
  //   Tab 3 — Alert: seçili sensöre kilitli ortak alarm konfigürasyonu
  // ============================================================
  registerPopupType('sensor-chart', async (config) => {
    const rootTag = config.rootTag || '';
    const displayName = config.title || config.displayName || rootTag;
    const calibMode = config.calibrationMode || 'none';
    const calib = config.calibration || {};
    const calibrationTabRequested = config.showCalibrationTab === true || config.showCalibrationTab === 'true' || config.showCalibrationTab === 1 || config.showCalibrationTab === '1';

    // Admin yetkisi var mı? (kalibrasyon sekmesi sadece admin için)
    let isAdmin = false;
    try {
      if (window.Auth && typeof window.Auth.isAdmin === 'function') {
        isAdmin = !!window.Auth.isAdmin();
      } else if (window.Auth && typeof window.Auth.getUserInfo === 'function') {
        const info = window.Auth.getUserInfo();
        const role = info && (info.Role || info.role || '');
        isAdmin = /admin/i.test(String(role));
      }
    } catch (e) { isAdmin = false; }
    const showCalibrationTab = isAdmin && calibrationTabRequested && (calibMode === 'soft' || calibMode === 'plc');

    // Kalibrasyon sekmesi içeriği (Faz 2: bilgi/preview; Faz 3/4: gerçek formlar)
    function buildCalibTab() {
      if (calibMode === 'none') {
        return `<div style="padding:18px;text-align:center;color:#8a8ea8;">
           <i class="fas fa-info-circle" style="font-size:24px;margin-bottom:8px;display:block;color:#3a3d52;"></i>
           <div style="font-size:13px;">Bu sensör için kalibrasyon yapılandırılmamış.</div>
           <div style="font-size:11px;margin-top:6px;">Admin panel → element seç → Kalibrasyon Modu</div>
         </div>`;
      }
      if (calibMode === 'soft') {
        const m  = calib.multiplier != null ? calib.multiplier : 1;
        const o  = calib.offset != null ? calib.offset : 0;
        const r1 = calib.minRaw != null ? calib.minRaw : 0;
        const r2 = calib.maxRaw != null ? calib.maxRaw : 0;
        const e1 = calib.minEng != null ? calib.minEng : 0;
        const e2 = calib.maxEng != null ? calib.maxEng : 0;
        return `<div style="padding:14px;">
           <div style="color:#3399ff;font-size:12px;font-weight:600;margin-bottom:8px;">
             <i class="fas fa-sliders-h"></i> SOFT KALİBRASYON (mevcut değerler — düzenleme Faz 3'te)
           </div>
           <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:12px;">
             <div style="background:#262940;padding:8px;border-radius:4px;"><div style="color:#8a8ea8;font-size:10px;">Multiplier</div><div style="color:#fff;font-size:14px;">${m}</div></div>
             <div style="background:#262940;padding:8px;border-radius:4px;"><div style="color:#8a8ea8;font-size:10px;">Offset</div><div style="color:#fff;font-size:14px;">${o}</div></div>
             <div style="background:#262940;padding:8px;border-radius:4px;"><div style="color:#8a8ea8;font-size:10px;">Min Raw → Max Raw</div><div style="color:#fff;font-size:14px;">${r1} → ${r2}</div></div>
             <div style="background:#262940;padding:8px;border-radius:4px;"><div style="color:#8a8ea8;font-size:10px;">Min Eng → Max Eng</div><div style="color:#fff;font-size:14px;">${e1} → ${e2}</div></div>
           </div>
         </div>`;
      }
      if (calibMode === 'plc') {
        const rootSuffix = calib.rootSuffix || '';
        const allFields = Array.isArray(calib.fields) ? calib.fields : [];
        // Bool tipler kalibrasyon write UI'ında gösterilmez (real/int)
        const fields = allFields.filter(f => (f.type || 'real').toLowerCase() !== 'bool');
        const skippedBoolCount = allFields.length - fields.length;
        const rootCalculated = rootSuffix && rootTag.endsWith(rootSuffix)
          ? rootTag.slice(0, -rootSuffix.length) : rootTag;
        const fieldsHtml = fields.length === 0
          ? `<div style="color:#e55353;font-size:12px;text-align:center;padding:10px;">⚠️ Yazılabilir alan yok. Admin panelden ekle.</div>`
          : `<table style="width:100%;font-size:12px;border-collapse:collapse;">
               <thead><tr style="color:#8a8ea8;font-size:11px;text-align:left;">
                 <th style="padding:6px 6px;">Etiket</th>
                 <th style="padding:6px 6px;width:140px;">Canlı Değer</th>
                 <th style="padding:6px 6px;width:130px;">Yeni Değer</th>
                 <th style="padding:6px 6px;width:90px;text-align:right;">Aksiyon</th>
               </tr></thead>
               <tbody>${fields.map((f, idx) => {
                 const writeTag = rootCalculated + (f.suffix || '');
                 const ftype = (f.type || 'real').toLowerCase();
                 const step = ftype === 'int' ? '1' : 'any';
                 return `
                 <tr style="border-top:1px solid #333654;" data-calib-row="${idx}" data-tag="${escapeHtml(writeTag)}" data-type="${escapeHtml(ftype)}">
                   <td style="padding:6px 6px;color:#fff;">
                     <div>${escapeHtml(f.label || '')}</div>
                     <div style="font-size:10px;color:#3399ff;font-family:monospace;">${escapeHtml(writeTag)}</div>
                   </td>
                   <td style="padding:6px 6px;">
                     <span class="cpopup-calib-live" data-row="${idx}" style="color:#2eb85c;font-family:monospace;font-size:13px;">—</span>
                   </td>
                   <td style="padding:6px 6px;">
                     <input type="number" step="${step}" class="cpopup-calib-input" data-row="${idx}"
                       style="width:100%;padding:4px 6px;background:#1a1d35;color:#fff;border:1px solid #333654;border-radius:3px;font-size:12px;font-family:monospace;" />
                   </td>
                   <td style="padding:6px 6px;text-align:right;">
                     <button class="cpopup-calib-write btn btn-primary btn-sm" data-row="${idx}"
                       style="font-size:11px;padding:4px 10px;" disabled>
                       <i class="fas fa-save"></i> YAZ
                     </button>
                   </td>
                 </tr>`;
               }).join('')}</tbody>
             </table>`;
        const skipNote = skippedBoolCount > 0
          ? `<div style="font-size:10px;color:#f9b115;margin-top:6px;">ℹ️ ${skippedBoolCount} adet bool alan gizlendi (kalibrasyonda yazılmaz).</div>` : '';
        return `<div style="padding:14px;">
           <div style="color:#3399ff;font-size:12px;font-weight:600;margin-bottom:8px;display:flex;justify-content:space-between;align-items:center;">
             <span><i class="fas fa-microchip"></i> PLC KALİBRASYON</span>
             <span style="display:flex;gap:8px;align-items:center;">
               <span id="cpopup-calib-status" style="font-size:10px;color:#8a8ea8;font-weight:400;">Yükleniyor...</span>
               <button id="cpopup-calib-refresh" class="btn btn-sm btn-ghost-primary" style="font-size:10px;padding:2px 8px;" title="Canlı değerleri tekrar oku">
                 <i class="fas fa-sync-alt"></i>
               </button>
             </span>
           </div>
           <div style="font-size:11px;color:#8a8ea8;margin-bottom:8px;">
             Kök: <code style="color:#3399ff;">${escapeHtml(rootCalculated || '(?)')}</code>
             · Suffix: <code>${escapeHtml(rootSuffix || '(boş)')}</code>
           </div>
           ${fieldsHtml}
           ${skipNote}
         </div>`;
      }
      return '';
    }

    const calibTabHtml = showCalibrationTab ? buildCalibTab() : '';

    return `
      <div class="cpopup-sensor-shell">
        <ul class="cpopup-tabs" style="display:flex;list-style:none;padding:0;margin:0 0 12px 0;border-bottom:1px solid #333654;gap:4px;">
          <li class="cpopup-tab active" data-tab="trend"
              style="padding:8px 16px;cursor:pointer;border-radius:6px 6px 0 0;background:#262940;color:#fff;font-size:13px;font-weight:600;">
            <i class="fas fa-chart-line" style="margin-right:6px;"></i>Trend (24h)
          </li>
          ${showCalibrationTab ? `<li class="cpopup-tab" data-tab="calib"
              style="padding:8px 16px;cursor:pointer;border-radius:6px 6px 0 0;background:transparent;color:#8a8ea8;font-size:13px;font-weight:600;">
            <i class="fas fa-sliders-h" style="margin-right:6px;"></i>Kalibrasyon
          </li>` : ''}
          <li class="cpopup-tab" data-tab="alert"
              style="padding:8px 16px;cursor:pointer;border-radius:6px 6px 0 0;background:transparent;color:#8a8ea8;font-size:13px;font-weight:600;">
            <i class="fas fa-bell" style="margin-right:6px;"></i>Alert
          </li>
        </ul>

        <div id="cpopup-sensor-tab-trend" class="cpopup-tab-pane" style="display:block;">
          <div id="cpopup-sensor-chart-loading" style="text-align:center;padding:40px;color:#8a8ea8;">
            <span class="cpopup-spinner"></span>
            <div style="margin-top:10px;font-size:13px;">Son 24 saatlik veri yükleniyor...</div>
          </div>
          <div id="cpopup-sensor-chart-error" style="display:none;padding:20px;text-align:center;color:#e55353;font-size:13px;"></div>
          <div id="cpopup-sensor-chart-wrap" style="display:none;position:relative;height:380px;">
            <canvas id="cpopup-sensor-chart-canvas"></canvas>
          </div>
        </div>

        ${showCalibrationTab ? `<div id="cpopup-sensor-tab-calib" class="cpopup-tab-pane" style="display:none;">
          ${calibTabHtml}
        </div>` : ''}

        <div id="cpopup-sensor-tab-alert" class="cpopup-tab-pane" style="display:none;">
          <div class="cpopup-alert-card">
            <div class="cpopup-alert-heading">
              <div>
                <div style="font-size:13px;font-weight:600;color:#fff;"><i class="fas fa-bell" style="color:#f9b115;margin-right:6px;"></i>Alert Configuration</div>
                <div id="cpopup-alert-status" style="font-size:11px;color:#8a8ea8;margin-top:3px;">Sekme açıldığında yüklenecek</div>
              </div>
              <button type="button" id="cpopup-alert-refresh" class="cpopup-alert-mini-btn" title="Alarm tanımını yenile">
                <i class="fas fa-sync-alt"></i>
              </button>
            </div>
            <div class="cpopup-alert-note">
              Bu form Ayarlar &gt; Alarm Tanımları ile aynı kaydı kullanır. Tag Name seçili sensöre sabitlenmiştir;
              kaydettiğiniz değişiklikler Alarm Tanımları ekranına doğrudan yansır.
            </div>

            <div id="cpopup-alert-form" class="cpopup-alert-grid">
              <div class="cpopup-alert-field span-8">
                <label for="cpopup-alert-tag">Tag Name <span style="color:#e55353;">*</span></label>
                <input id="cpopup-alert-tag" class="cpopup-alert-input" type="text" value="${escapeHtml(rootTag)}" readonly>
              </div>
              <div class="cpopup-alert-field span-4">
                <label for="cpopup-alert-group">Alarm Group</label>
                <div class="cpopup-alert-select-row">
                  <select id="cpopup-alert-group" class="cpopup-alert-select"><option value="0">— (yok)</option></select>
                  <button type="button" id="cpopup-alert-groups-refresh" class="cpopup-alert-mini-btn" title="Grupları yenile"><i class="fas fa-sync-alt"></i></button>
                </div>
              </div>

              <div class="cpopup-alert-field">
                <label for="cpopup-alert-message">Message</label>
                <input id="cpopup-alert-message" class="cpopup-alert-input" type="text" placeholder="Alarm mesajı">
              </div>
              <div class="cpopup-alert-field span-6">
                <label for="cpopup-alert-delay">Delay (saniye)</label>
                <input id="cpopup-alert-delay" class="cpopup-alert-input" type="number" value="0" min="0" step="0.1">
              </div>
              <div class="cpopup-alert-field span-3" style="align-self:end;">
                <label class="cpopup-alert-check"><input id="cpopup-alert-enable" type="checkbox" checked> Enable</label>
              </div>
              <div class="cpopup-alert-field span-3" style="align-self:end;">
                <label class="cpopup-alert-check" title="Bool tag'lar için TRUE/FALSE mantığını ters çevirir"><input id="cpopup-alert-reverse" type="checkbox"> Reverse</label>
              </div>

              <div class="cpopup-alert-section">Setpoints (Numerik tag'lar için)<span style="display:block;font-size:10px;color:#8a8ea8;margin-top:3px;">Yalnızca işaretli eşikler dikkate alınır.</span></div>
              <div class="cpopup-alert-field span-3">
                <label for="cpopup-alert-ll" style="color:#e55353;"><input id="cpopup-alert-ll-enabled" type="checkbox" style="margin-right:5px;">LL (Low Low)</label>
                <input id="cpopup-alert-ll" class="cpopup-alert-input" type="number" value="0" step="any">
              </div>
              <div class="cpopup-alert-field span-3">
                <label for="cpopup-alert-l" style="color:#f9b115;"><input id="cpopup-alert-l-enabled" type="checkbox" style="margin-right:5px;">L (Low)</label>
                <input id="cpopup-alert-l" class="cpopup-alert-input" type="number" value="0" step="any">
              </div>
              <div class="cpopup-alert-field span-3">
                <label for="cpopup-alert-h" style="color:#f9b115;"><input id="cpopup-alert-h-enabled" type="checkbox" style="margin-right:5px;">H (High)</label>
                <input id="cpopup-alert-h" class="cpopup-alert-input" type="number" value="0" step="any">
              </div>
              <div class="cpopup-alert-field span-3">
                <label for="cpopup-alert-hh" style="color:#e55353;"><input id="cpopup-alert-hh-enabled" type="checkbox" style="margin-right:5px;">HH (High High)</label>
                <input id="cpopup-alert-hh" class="cpopup-alert-input" type="number" value="0" step="any">
              </div>

              <div class="cpopup-alert-section">Bildirim Kanalları</div>
              <div class="cpopup-alert-field span-3"><label class="cpopup-alert-check"><input id="cpopup-alert-send-email" type="checkbox"> Alarm → Mail</label></div>
              <div class="cpopup-alert-field span-3"><label class="cpopup-alert-check"><input id="cpopup-alert-send-sms" type="checkbox"> Alarm → SMS</label></div>
              <div class="cpopup-alert-field span-3"><label class="cpopup-alert-check"><input id="cpopup-alert-warn-email" type="checkbox"> Warning → Mail</label></div>
              <div class="cpopup-alert-field span-3"><label class="cpopup-alert-check"><input id="cpopup-alert-warn-sms" type="checkbox"> Warning → SMS</label></div>

              <div class="cpopup-alert-section">E-posta Tekrar ve Düzelme Politikası</div>
              <div class="cpopup-alert-field span-6" style="align-self:end;"><label class="cpopup-alert-check" title="Alarm aktif kaldığı sürece seviyeye göre tekrar e-postası gönderir"><input id="cpopup-alert-email-repeat" type="checkbox" checked> Tekrar maili</label></div>
              <div class="cpopup-alert-field span-6" style="align-self:end;"><label class="cpopup-alert-check" title="Alarm normale döndüğünde bir kez düzelme e-postası gönderir"><input id="cpopup-alert-recovery-email" type="checkbox" checked> Düzelme maili</label></div>
              <div class="cpopup-alert-field span-6">
                <label for="cpopup-alert-warning-repeat">Warning tekrarı (dk)</label>
                <input id="cpopup-alert-warning-repeat" class="cpopup-alert-input" type="number" value="60" min="0.01" step="any">
              </div>
              <div class="cpopup-alert-field span-6">
                <label for="cpopup-alert-critical-repeat">Kritik tekrarı (dk)</label>
                <input id="cpopup-alert-critical-repeat" class="cpopup-alert-input" type="number" value="15" min="0.01" step="any">
              </div>
            </div>

            <div class="cpopup-alert-actions">
              <button type="button" id="cpopup-alert-reset" class="cpopup-alert-action secondary">İptal</button>
              <button type="button" id="cpopup-alert-save" class="cpopup-alert-action primary"><i class="fas fa-save" style="margin-right:5px;"></i>Kaydet</button>
            </div>
          </div>
        </div>
      </div>

      <script>
        (function() {
          const rootTag = ${JSON.stringify(rootTag)};
          const displayName = ${JSON.stringify(displayName)};
          let chartInstance = null;
          let alertLoaded = false;
          let alertLoading = false;
          let currentAlertConfig = null;

          // Tab değiştirme
          const tabs = document.querySelectorAll('.cpopup-tab');
          tabs.forEach(t => {
            t.onclick = () => {
              tabs.forEach(x => {
                x.classList.remove('active');
                x.style.background = 'transparent';
                x.style.color = '#8a8ea8';
              });
              t.classList.add('active');
              t.style.background = '#262940';
              t.style.color = '#fff';
              const which = t.getAttribute('data-tab');
              document.getElementById('cpopup-sensor-tab-trend').style.display = (which === 'trend') ? 'block' : 'none';
              const calibrationPane = document.getElementById('cpopup-sensor-tab-calib');
              if (calibrationPane) calibrationPane.style.display = (which === 'calib') ? 'block' : 'none';
              document.getElementById('cpopup-sensor-tab-alert').style.display = (which === 'alert') ? 'block' : 'none';
              if (which === 'alert' && !alertLoaded) loadAlertConfig(false);
            };
          });

          // Tarih formatı: "YYYY-MM-DD HH:mm" (mevcut chart endpoint formatı)
          function fmt(d) {
            const yy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            const hh = String(d.getHours()).padStart(2, '0');
            const mi = String(d.getMinutes()).padStart(2, '0');
            return yy + '-' + mm + '-' + dd + ' ' + hh + ':' + mi;
          }

          // 24h verisini çek
          async function loadTrend() {
            const loadingEl = document.getElementById('cpopup-sensor-chart-loading');
            const errorEl   = document.getElementById('cpopup-sensor-chart-error');
            const wrapEl    = document.getElementById('cpopup-sensor-chart-wrap');

            if (typeof Chart === 'undefined') {
              loadingEl.style.display = 'none';
              errorEl.style.display = 'block';
              errorEl.textContent = 'Chart.js yüklenmemiş.';
              return;
            }

            const toDate   = new Date();
            const fromDate = new Date(toDate.getTime() - 24 * 60 * 60 * 1000);

            try {
              const resp = await fetch('/api/v1/chart/multi-tags-data', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  tagNameArray: rootTag,
                  fromDate: fmt(fromDate),
                  toDate:   fmt(toDate),
                  freq: 'Hour'
                })
              });

              if (!resp.ok) {
                const errTxt = await resp.text().catch(() => '');
                throw new Error('API ' + resp.status + (errTxt ? ' — ' + errTxt.slice(0, 200) : ''));
              }
              const json = await resp.json();
              const data = (json && json.data) || [];

              if (!data.length) {
                loadingEl.style.display = 'none';
                errorEl.style.display = 'block';
                errorEl.textContent = 'Son 24 saat için veri bulunamadı.';
                return;
              }

              // Bu tag'a ait satırları filtrele (API tek tag istesek de bazen başka tag dönebilir)
              const rows = data.filter(r => !r.TagName || r.TagName === rootTag);
              let prevDay = '';
              const labels = rows.map(r => {
                const ts = r.Date || r.TimeStamp || r.date || r.timestamp;
                if (!ts) return '';
                // "YYYY-MM-DD HH:mm" veya ISO string -> gün değişimi varsa "DD/MM HH:mm", aksi halde "HH:mm"
                const m = String(ts).match(/(\\d{4})-(\\d{2})-(\\d{2})[ T](\\d{2}):(\\d{2})/);
                if (m) {
                  const dayKey = m[3] + '/' + m[2];
                  const hhmm = m[4] + ':' + m[5];
                  if (dayKey !== prevDay) {
                    prevDay = dayKey;
                    return dayKey + ' ' + hhmm;
                  }
                  return hhmm;
                }
                return String(ts);
              });
              const values = rows.map(r => {
                const v = (r.Value !== undefined) ? r.Value : (r.value !== undefined ? r.value : null);
                return v === null ? null : Number(v);
              });

              loadingEl.style.display = 'none';
              wrapEl.style.display = 'block';

              const canvas = document.getElementById('cpopup-sensor-chart-canvas');
              if (chartInstance) { chartInstance.destroy(); chartInstance = null; }

              chartInstance = new Chart(canvas.getContext('2d'), {
                type: 'line',
                data: {
                  labels: labels,
                  datasets: [{
                    label: displayName,
                    data: values,
                    borderColor: 'rgb(51, 153, 255)',
                    backgroundColor: 'rgba(51, 153, 255, 0.15)',
                    tension: 0.3,
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    fill: true,
                    spanGaps: true
                  }]
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { labels: { color: '#e4e6eb' } },
                    tooltip: { mode: 'index', intersect: false }
                  },
                  scales: {
                    x: {
                      ticks: { color: '#8a8ea8', maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
                      grid:  { color: 'rgba(255,255,255,0.05)' }
                    },
                    y: {
                      ticks: { color: '#8a8ea8' },
                      grid:  { color: 'rgba(255,255,255,0.05)' }
                    }
                  }
                }
              });
            } catch (err) {
              console.error('[sensor-chart] Trend yüklenemedi:', err);
              loadingEl.style.display = 'none';
              errorEl.style.display = 'block';
              errorEl.textContent = 'Veri yüklenirken hata oluştu: ' + (err.message || err);
            }
          }

          loadTrend();

          // ============================================================
          // Alert Configuration — Ayarlar > Alarm Tanımları ile ortak kayıt
          // ============================================================
          const alertPane = document.getElementById('cpopup-sensor-tab-alert');
          const alertStatus = document.getElementById('cpopup-alert-status');
          const alertSaveBtn = document.getElementById('cpopup-alert-save');

          function setAlertStatus(text, color) {
            if (!alertStatus) return;
            alertStatus.textContent = text;
            alertStatus.style.color = color || '#8a8ea8';
          }

          function setAlertFormDisabled(disabled) {
            if (!alertPane) return;
            alertPane.querySelectorAll('input:not([readonly]), select, #cpopup-alert-save, #cpopup-alert-reset')
              .forEach(el => { el.disabled = disabled; });
          }

          function alertNumber(id) {
            const value = document.getElementById(id)?.value;
            const number = parseFloat(value);
            return Number.isFinite(number) ? number : 0;
          }

          function alertChecked(id) {
            return !!document.getElementById(id)?.checked;
          }

          function fillAlertForm(item) {
            const thresholdEnabled = (level) => {
              const key = level + '_enabled';
              if (item && Object.prototype.hasOwnProperty.call(item, key)) {
                const raw = item[key];
                return raw === true || raw === 1 || /^(true|1|yes|evet)$/i.test(String(raw));
              }
              const threshold = Number(item && item[level + '_set']);
              return Number.isFinite(threshold) && threshold !== 0;
            };
            const value = (id, val) => {
              const el = document.getElementById(id);
              if (el) el.value = val ?? '';
            };
            const checked = (id, val) => {
              const el = document.getElementById(id);
              if (el) el.checked = !!val;
            };
            value('cpopup-alert-tag', rootTag);
            value('cpopup-alert-group', item ? (item.alarm_group_id ?? 0) : 0);
            value('cpopup-alert-message', item ? item.message : '');
            value('cpopup-alert-delay', item ? item.delay : 0);
            checked('cpopup-alert-enable', item ? item.enable : true);
            checked('cpopup-alert-reverse', item ? item.reverse : false);
            value('cpopup-alert-ll', item ? item.ll_set : 0);
            value('cpopup-alert-l', item ? item.l_set : 0);
            value('cpopup-alert-h', item ? item.h_set : 0);
            value('cpopup-alert-hh', item ? item.hh_set : 0);
            checked('cpopup-alert-ll-enabled', item ? thresholdEnabled('ll') : false);
            checked('cpopup-alert-l-enabled', item ? thresholdEnabled('l') : false);
            checked('cpopup-alert-h-enabled', item ? thresholdEnabled('h') : false);
            checked('cpopup-alert-hh-enabled', item ? thresholdEnabled('hh') : false);
            checked('cpopup-alert-send-email', item ? item.send_email : false);
            checked('cpopup-alert-send-sms', item ? item.send_sms : false);
            checked('cpopup-alert-warn-email', item ? item.warn_send_email : false);
            checked('cpopup-alert-warn-sms', item ? item.warn_send_sms : false);
            checked('cpopup-alert-email-repeat', item ? item.email_repeat_enabled !== false : true);
            value('cpopup-alert-warning-repeat', item ? (item.warning_repeat_minutes ?? 60) : 60);
            value('cpopup-alert-critical-repeat', item ? (item.critical_repeat_minutes ?? 15) : 15);
            checked('cpopup-alert-recovery-email', item ? item.send_recovery_email !== false : true);
          }

          function extractAlertConfigs(data) {
            if (Array.isArray(data)) return data;
            if (data && Array.isArray(data.alerts)) return data.alerts;
            if (data && Array.isArray(data.data)) return data.data;
            if (data && Array.isArray(data.configs)) return data.configs;
            return [];
          }

          function renderAlertGroups(groups) {
            const select = document.getElementById('cpopup-alert-group');
            if (!select) return;
            const selected = String(
              !alertLoaded && currentAlertConfig
                ? (currentAlertConfig.alarm_group_id ?? 0)
                : (select.value || (currentAlertConfig ? (currentAlertConfig.alarm_group_id ?? 0) : 0))
            );
            select.replaceChildren();
            const none = document.createElement('option');
            none.value = '0';
            none.textContent = '— (yok)';
            select.appendChild(none);
            groups.forEach(group => {
              const option = document.createElement('option');
              option.value = String(group.id);
              option.textContent = String(group.id) + ' — ' + String(group.name || '') +
                ' (' + (group.emails || []).length + 'm / ' + (group.phones || []).length + 't)';
              select.appendChild(option);
            });
            if (selected !== '0' && !groups.some(group => String(group.id) === selected)) {
              const current = document.createElement('option');
              current.value = selected;
              current.textContent = selected + ' — (mevcut grup)';
              select.appendChild(current);
            }
            select.value = selected;
            if (select.value !== selected) select.value = '0';
          }

          async function fetchAlertGroups() {
            const response = await fetch('/api/v1/notifications/groups');
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error((data && (data.error || data.message)) || 'Gruplar yüklenemedi (HTTP ' + response.status + ')');
            if (Array.isArray(data)) return data;
            return (data && Array.isArray(data.groups)) ? data.groups : [];
          }

          async function loadAlertGroups(showResult) {
            const refresh = document.getElementById('cpopup-alert-groups-refresh');
            if (refresh) refresh.disabled = true;
            try {
              const groups = await fetchAlertGroups();
              renderAlertGroups(groups);
              if (showResult) window._cpopupNotify('Alarm grupları yenilendi', 'success');
            } catch (err) {
              renderAlertGroups([]);
              if (showResult) window._cpopupNotify(err.message || String(err), 'warning');
            } finally {
              if (refresh) refresh.disabled = false;
            }
          }

          async function loadAlertConfig(forceGroups) {
            if (alertLoading) return;
            if (!rootTag) {
              setAlertStatus('Sensör tag adı bulunamadı', '#e55353');
              setAlertFormDisabled(true);
              return;
            }
            alertLoading = true;
            const refresh = document.getElementById('cpopup-alert-refresh');
            if (refresh) refresh.disabled = true;
            setAlertFormDisabled(true);
            setAlertStatus('Alarm tanımı yükleniyor...', '#8a8ea8');
            try {
              const response = await fetch('/api/v1/alerts/config');
              const data = await response.json().catch(() => ({}));
              if (!response.ok) throw new Error((data && (data.error || data.message)) || 'Alarm tanımı yüklenemedi (HTTP ' + response.status + ')');
              const configs = extractAlertConfigs(data);
              currentAlertConfig = configs.find(item => item && item.tag_name === rootTag) || null;
              await loadAlertGroups(!!forceGroups);
              fillAlertForm(currentAlertConfig);
              alertLoaded = true;
              setAlertStatus(
                currentAlertConfig ? 'Mevcut alarm tanımı yüklendi' : 'Bu sensör için yeni alarm tanımı',
                currentAlertConfig ? '#2eb85c' : '#3399ff'
              );
              setAlertFormDisabled(false);
            } catch (err) {
              console.error('[sensor-chart alert] load err:', err);
              alertLoaded = false;
              setAlertStatus('Yükleme hatası: ' + (err.message || err), '#e55353');
              setAlertFormDisabled(true);
            } finally {
              alertLoading = false;
              if (refresh) refresh.disabled = false;
            }
          }

          async function saveAlertConfig() {
            const warningRepeatMinutes = alertNumber('cpopup-alert-warning-repeat');
            const criticalRepeatMinutes = alertNumber('cpopup-alert-critical-repeat');
            if (warningRepeatMinutes <= 0 || criticalRepeatMinutes <= 0) {
              setAlertStatus('Warning ve kritik tekrar süreleri sıfırdan büyük olmalıdır', '#e55353');
              window._cpopupNotify('Geçersiz e-posta tekrar süresi', 'warning');
              return;
            }
            const payload = {
              tag_name: rootTag,
              alarm_group_id: parseInt(alertNumber('cpopup-alert-group'), 10) || 0,
              delay: alertNumber('cpopup-alert-delay'),
              enable: alertChecked('cpopup-alert-enable'),
              reverse: alertChecked('cpopup-alert-reverse'),
              ll_set: alertNumber('cpopup-alert-ll'),
              l_set: alertNumber('cpopup-alert-l'),
              h_set: alertNumber('cpopup-alert-h'),
              hh_set: alertNumber('cpopup-alert-hh'),
              ll_enabled: alertChecked('cpopup-alert-ll-enabled'),
              l_enabled: alertChecked('cpopup-alert-l-enabled'),
              h_enabled: alertChecked('cpopup-alert-h-enabled'),
              hh_enabled: alertChecked('cpopup-alert-hh-enabled'),
              message: document.getElementById('cpopup-alert-message')?.value || '',
              send_email: alertChecked('cpopup-alert-send-email'),
              send_sms: alertChecked('cpopup-alert-send-sms'),
              warn_send_email: alertChecked('cpopup-alert-warn-email'),
              warn_send_sms: alertChecked('cpopup-alert-warn-sms'),
              email_repeat_enabled: alertChecked('cpopup-alert-email-repeat'),
              warning_repeat_minutes: warningRepeatMinutes,
              critical_repeat_minutes: criticalRepeatMinutes,
              send_recovery_email: alertChecked('cpopup-alert-recovery-email')
            };
            const wasExisting = !!currentAlertConfig;
            const originalHtml = alertSaveBtn.innerHTML;
            setAlertFormDisabled(true);
            alertSaveBtn.innerHTML = '<span class="cpopup-spinner" style="width:12px;height:12px;margin-right:6px;"></span>' +
              (wasExisting ? 'Güncelleniyor...' : 'Kaydediliyor...');
            setAlertStatus(wasExisting ? 'Alarm tanımı güncelleniyor...' : 'Alarm tanımı kaydediliyor...', '#f9b115');
            try {
              const url = wasExisting ? '/api/v1/alerts/config/' + encodeURIComponent(rootTag) : '/api/v1/alerts/config';
              const response = await fetch(url, {
                method: wasExisting ? 'PATCH' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
              });
              const data = await response.json().catch(() => ({}));
              if (!response.ok || data.success === false) {
                throw new Error((data && (data.error || data.message)) || 'Alarm tanımı kaydedilemedi (HTTP ' + response.status + ')');
              }
              currentAlertConfig = payload;
              alertLoaded = true;
              fillAlertForm(currentAlertConfig);
              setAlertStatus('Alarm tanımı kaydedildi', '#2eb85c');
              window._cpopupNotify((wasExisting ? 'Alarm tanımı güncellendi: ' : 'Alarm tanımı eklendi: ') + rootTag, 'success');
            } catch (err) {
              console.error('[sensor-chart alert] save err:', err);
              setAlertStatus('Kaydetme hatası: ' + (err.message || err), '#e55353');
              window._cpopupNotify(err.message || String(err), 'error');
            } finally {
              setAlertFormDisabled(false);
              alertSaveBtn.innerHTML = originalHtml;
            }
          }

          document.getElementById('cpopup-alert-refresh')?.addEventListener('click', () => loadAlertConfig(true));
          document.getElementById('cpopup-alert-groups-refresh')?.addEventListener('click', () => loadAlertGroups(true));
          document.getElementById('cpopup-alert-reset')?.addEventListener('click', () => {
            window._cpopupManager.close();
          });
          alertSaveBtn?.addEventListener('click', saveAlertConfig);

          // ============================================================
          // PLC Kalibrasyon — canlı okuma + yazma (Faz 4)
          // ============================================================
          const _calibMode = ${JSON.stringify(calibMode)};
          const _calibIsAdmin = ${JSON.stringify(isAdmin)};
          const _showCalibrationTab = ${JSON.stringify(showCalibrationTab)};
          if (_showCalibrationTab && _calibMode === 'plc' && _calibIsAdmin) {
            const rows = Array.from(document.querySelectorAll('[data-calib-row]'));
            const statusEl = document.getElementById('cpopup-calib-status');
            const tabContainer = document.getElementById('cpopup-sensor-tab-calib');
            // Tag → row index map
            const tagsToRead = rows.map(r => r.getAttribute('data-tag'));
            const lastLiveValue = {}; // tag → number

            function setStatus(text, color) {
              if (statusEl) {
                statusEl.textContent = text;
                statusEl.style.color = color || '#8a8ea8';
              }
            }

            function fmtNum(v, type) {
              if (v === null || v === undefined || isNaN(v)) return '—';
              if (type === 'int') return String(Math.round(Number(v)));
              const n = Number(v);
              return (Math.abs(n) >= 1000 ? n.toFixed(2) : n.toFixed(3)).replace(/\.?0+$/, '');
            }

            function updateButtonState(row) {
              const idx = row.getAttribute('data-calib-row');
              const input = document.querySelector('.cpopup-calib-input[data-row="' + idx + '"]');
              const btn   = document.querySelector('.cpopup-calib-write[data-row="' + idx + '"]');
              if (!input || !btn) return;
              const tag = row.getAttribute('data-tag');
              const live = lastLiveValue[tag];
              const val = input.value.trim();
              if (val === '' || isNaN(Number(val))) { btn.disabled = true; return; }
              if (live != null && Number(val) === Number(live)) { btn.disabled = true; return; }
              btn.disabled = false;
            }

            async function readLiveValues() {
              if (!document.body.contains(tabContainer)) return false; // popup kapandı
              try {
                const resp = await window._cpopupReadTags(tagsToRead);
                if (resp && resp.success && resp.tags) {
                  rows.forEach(row => {
                    const idx = row.getAttribute('data-calib-row');
                    const tag = row.getAttribute('data-tag');
                    const type = row.getAttribute('data-type');
                    const entry = resp.tags[tag];
                    const v = entry && (entry.value !== undefined ? entry.value : entry);
                    const liveCell = document.querySelector('.cpopup-calib-live[data-row="' + idx + '"]');
                    if (v === null || v === undefined) {
                      if (liveCell) { liveCell.textContent = 'YOK'; liveCell.style.color = '#e55353'; }
                      return;
                    }
                    const num = Number(v);
                    if (isNaN(num)) {
                      if (liveCell) { liveCell.textContent = String(v); liveCell.style.color = '#f9b115'; }
                      return;
                    }
                    lastLiveValue[tag] = num;
                    if (liveCell) { liveCell.textContent = fmtNum(num, type); liveCell.style.color = '#2eb85c'; }
                    // İlk yükleme: input boşsa canlı değeri öneri olarak yerleştir
                    const input = document.querySelector('.cpopup-calib-input[data-row="' + idx + '"]');
                    if (input && input.value === '' && !input.dataset.userTouched) {
                      input.value = fmtNum(num, type);
                    }
                    updateButtonState(row);
                  });
                  setStatus('Canlı · ' + new Date().toLocaleTimeString('tr-TR'), '#2eb85c');
                  return true;
                } else {
                  setStatus('Okuma başarısız', '#e55353');
                }
              } catch (e) {
                console.error('[sensor-chart calib] read err:', e);
                setStatus('Bağlantı hatası', '#e55353');
              }
              return false;
            }

            // Input change → butonu enable/disable + userTouched işaretle
            document.querySelectorAll('.cpopup-calib-input').forEach(inp => {
              inp.addEventListener('input', () => {
                inp.dataset.userTouched = '1';
                const row = inp.closest('[data-calib-row]');
                if (row) updateButtonState(row);
              });
            });

            // Write butonları
            document.querySelectorAll('.cpopup-calib-write').forEach(btn => {
              btn.addEventListener('click', async () => {
                const idx = btn.getAttribute('data-row');
                const row = document.querySelector('[data-calib-row="' + idx + '"]');
                const input = document.querySelector('.cpopup-calib-input[data-row="' + idx + '"]');
                if (!row || !input) return;
                const tag = row.getAttribute('data-tag');
                const type = row.getAttribute('data-type');
                const rawVal = input.value.trim();
                if (rawVal === '' || isNaN(Number(rawVal))) {
                  window._cpopupNotify('Geçerli bir sayı giriniz', 'warning'); return;
                }
                const newVal = type === 'int' ? Math.round(Number(rawVal)) : Number(rawVal);
                const oldVal = lastLiveValue[tag];
                const labelCell = row.querySelector('td:first-child > div:first-child');
                const label = labelCell ? labelCell.textContent.trim() : tag;
                const writeFn = (type === 'int')
                  ? () => window._cpopupWriteInt(tag, newVal, null, oldVal, label)
                  : () => window._cpopupWriteReal(tag, newVal, null, oldVal, label);
                const ok = await window._cpopupWriteAndVerify(
                  btn, writeFn, tag, newVal,
                  label + ' = ' + newVal + ' yazıldı', label + ' yazılamadı'
                );
                if (ok) {
                  lastLiveValue[tag] = newVal;
                  delete input.dataset.userTouched;
                  // Yeniden oku
                  setTimeout(readLiveValues, 300);
                }
              });
            });

            // İlk okuma (popup açılışta tek seferlik)
            readLiveValues();

            // Yenile butonu
            const refreshBtn = document.getElementById('cpopup-calib-refresh');
            if (refreshBtn) {
              refreshBtn.addEventListener('click', () => {
                setStatus('Okunuyor...', '#8a8ea8');
                readLiveValues();
              });
            }
          }
        })();
      </script>
    `;
  });

  // ============================================================
  // Global Instance + Window Exports
  // ============================================================

  const manager = new ControlPopupManager();

  // Expose to window for inline scripts and external access
  window._cpopupManager = manager;
  window._cpopupWriteBool = plcWriteBool;
  window._cpopupWriteReal = plcWriteReal;
  window._cpopupWriteInt = plcWriteInt;
  window._cpopupReadTags = plcReadTags;
  window._cpopupWriteAndVerify = writeAndVerify;
  window._cpopupSetToggle = setToggleState;
  window._cpopupNotify = showNotify;
  window._cpopupConfirm = showConfirm;
  window._cpopupWriteBatch = plcWriteBatch;
  window._cpopupNotifyBatchResult = notifyBatchResult;

  // Public API
  window.ControlPopupManager = manager;

  // ============================================================
  // WebSocket: Başka kullanıcıların PLC yazma bildirimlerini dinle
  // ============================================================
  function initWriteNotifications() {
    if (typeof io === 'undefined') return;
    try {
      const socket = io({
        auth: {
          token: window.Auth && typeof window.Auth.getToken === 'function'
            ? window.Auth.getToken()
            : null
        }
      });
      socket.on('plc:write', function(data) {
        const currentUser = getCurrentUser();
        if (data.user && data.user !== currentUser) {
          showNotify(`${data.user} → ${data.tagName} = ${data.value}`, 'info');
        }
      });
    } catch(e) { /* Socket.IO bağlantısı yoksa sessizce geç */ }
  }

  // Sayfa yüklenince başlat
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWriteNotifications);
  } else {
    initWriteNotifications();
  }

})();
